// src/MessageList.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import styles from './ChatBotComponent.module.css';
import { ReactComponent as ChatBotIcon } from '../../Assets/svgs/chat bot icon.svg';
import { ReactComponent as HumanAvatarIcon } from '../../Assets/svgs/human avatar icon.svg';
import aiavatar from '../../Assets/svgs/ai-chat-icon.svg';
import { ReactComponent as FillThumbsDown } from '../../Assets/svgs/hand-thumbs-down-fill.svg';
import { ReactComponent as FillThumbsUp } from '../../Assets/svgs/hand-thumbs-up-fill.svg';
import { ReactComponent as ThumbsDown } from '../../Assets/svgs/hand-thumbs-down.svg';
import { ReactComponent as ThumbsUp } from '../../Assets/svgs/hand-thumbs-up.svg';
import { ReactComponent as AiInsights } from '../../Assets/svgs/ai_insights.svg';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import Tooltip from "@mui/material/Tooltip";
import {
  filterOptions,
  R_AND_D_AUTO_SUGGESTIONS,
  restrictedUsers,
  variationData,
} from '../../Common/Constants';
import AdditionalOptions from './AdditionalOptions/AdditionalOptions';
import { marked } from 'marked';
import { callLogEvent, highlightPatterns } from '../../Common/Util';
import DOMPurify from 'dompurify';
import ReorderIcon from '@mui/icons-material/Reorder';
import DWDModal from '../../Common/Components/DWDModal/DWDModal';
import {
  deleteSavedChats,
  getFiltersApi,
  saveChat,
  saveFeedback,
  saveLikeMessage,
  getFiltersBySelection,
} from '../../Services/chat-bot-service';
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  Tooltip,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Checkbox,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  Drawer,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MenuIcon from '@mui/icons-material/Menu';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
// import { IconButton, Stack, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress } from '@mui/material';
import tipsIcon from '../../Assets/Images/tips.png';
import RenderSummary from './RenderSummary';
import TreeVisualization from './DataTree/DataTree';
import RenderSummary_v2 from './RenderSummary_v2';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../Store/Store';
import { updateTitle, updateTreeInfo } from '../../Store/chatBotSlice';
import MonthRangePicker from './Calendarview/MonthRangPicker';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterHelp from './FilterHelp';
import { getInsightSectionHelpText } from './FilterHelp';

// ---------------------------------------------------------------------------
// StreamingBubble – self-contained typing animation.
// Writes characters directly to a DOM ref; zero React state updates per char.
// ---------------------------------------------------------------------------
const StreamingBubble = React.memo(function StreamingBubble({
  isIdeal,
}: {
  isIdeal: boolean;
}) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isIdeal) {
      if (spanRef.current) spanRef.current.textContent = '';
      return;
    }

    const random = Math.floor(Math.random() * 4) + 1;
    const variations: string[] =
      variationData['Variation ' + random] || ['Analyzing your query...'];

    let varIdx = 0;
    let charIdx = 0;
    let mounted = true;
    let rafId: number;
    let lastTime = 0;
    // Type one character every ~30ms (≈33 chars/sec) using rAF so the
    // ticker is never blocked or batched by React renders.
    const CHAR_INTERVAL_MS = 30;
    const PAUSE_AFTER_PHRASE_MS = 900;

    const tick = (now: number) => {
      if (!mounted) return;
      if (now - lastTime >= CHAR_INTERVAL_MS) {
        lastTime = now;
        const current = variations[varIdx % variations.length];
        if (charIdx <= current.length) {
          if (spanRef.current) {
            spanRef.current.textContent = current.substring(0, charIdx);
          }
          charIdx++;
          rafId = requestAnimationFrame(tick);
        } else {
          // Phrase fully typed – pause then advance to next phrase.
          const pauseStart = now;
          const waitForPause = (t: number) => {
            if (!mounted) return;
            if (t - pauseStart >= PAUSE_AFTER_PHRASE_MS) {
              charIdx = 0;
              varIdx++;
              lastTime = t;
              rafId = requestAnimationFrame(tick);
            } else {
              rafId = requestAnimationFrame(waitForPause);
            }
          };
          rafId = requestAnimationFrame(waitForPause);
        }
      } else {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      mounted = false;
      cancelAnimationFrame(rafId);
      if (spanRef.current) spanRef.current.textContent = '';
    };
  }, [isIdeal]);

  if (isIdeal) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        marginTop: 6,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: 24,
          minHeight: 24,
          flexShrink: 0,
          marginTop: 4,
        }}
        aria-hidden
      >
        <ChatBotIcon style={{ width: 20, height: 20 }} />
      </span>
      <span
        ref={spanRef}
        style={{
          margin: '8px 0',
          flex: 1,
          minWidth: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: '#4b5563',
          fontStyle: 'italic',
          opacity: 0.92,
          fontSize: 'inherit',
        }}
      />
    </div>
  );
});

export interface MessageListProps {
  // uiMode selects which part of the component should render: summary (main page) or chat (drawer)
  uiMode: 'summary' | 'chat' | string;
  messages: any;
  handleChange?: (event: any) => any;
  setInputValue?: any;
  setSuggestions?: any;
  isIdeal?: any;
  onSend?: any;
  title: any;
  disableSummaryBtn: any;
  handleSummary2?: (event: any) => any;
  handleSummary?: (event: any) => any;
  tipVisible?: boolean;
  streamingText?: string;
  allMessages?: any; // Full messages array for pin/delete operations (when messages is filtered)
  onPinToggle?: (messageId: string, isPinned: boolean) => void; // Callback for pin/delete that operates on full array
  onDeleteMessage?: (messageId: string) => void;
  activeInsightSection?: string | null; // For UAE MT insights selection (header-based)
  setActiveInsightSection?: (section: string | null) => void; // For UAE MT insights selection (header-based)
  isSummaryHistoryOpen?: boolean; // For UAE MT history drawer (header-based)
  setIsSummaryHistoryOpen?: (open: boolean) => void; // For UAE MT history drawer (header-based)
  isInsightsNavOpen?: boolean;
  setIsInsightsNavOpen?: (open: boolean) => void;
  isFilterDrawerOpen?: boolean;
  setIsFilterDrawerOpen?: (open: boolean) => void;
  showFilterHelp?: boolean;
  setShowFilterHelp?: (open: boolean) => void;
  onSummaryToolbarStateChange?: (state: {
    syncFilters: boolean;
    isSummaryGenerating: boolean;
  }) => void;
  onSummaryContextChange?: (context: {
    activeInsightSection: string | null;
    activeSummaryMessage: any | null;
  }) => void;
}

interface FilterGroup {
  [optionKey: string]: any;
}

interface ApiResponse {
  year_month: string[];
  [key: string]: any;
}

interface FilterOptions {
  [category: string]: FilterGroup;
}

interface Filters {
  // comparison: string;
  // year: number;
  // month: number;
  category: string[];
  region: string[];
  retailer: string[];
  subCategory: string[];
  use_cache: string;
}
type FiltersState = {
  base_start_month: string;
  base_end_month: string;
  comp_start_month: string;
  comp_end_month: string;
  region: string[];
  sub_region: string[];
  retailer: string[];
  city: string[];
  manufacturer: string[];
  brand: string[];
  sub_brand: string[];
  channel: string[];
  retailer_store: string[];
  promo_flag: string[];
  pack_group: string[];
  pack_size_group: string[];
  price_point: string[];
  category: string[];
  subCategory: string[];
  use_cache: string;
};

interface FilterDropdownProps {
  label: string;
  entries: Array<[string, string]>; // [displayLabel, value]
  value: string[];
  onToggle: (val: string) => void;
  onClear: () => void;
  onSelectAll?: () => void;
  placeholder?: string;
  menuProps?: any;
  disabled?: boolean;
  sx?: any;
  searchable?: boolean;
  forceMultipleLabel?: boolean;
  isExplicitAll?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  entries,
  value,
  onToggle,
  onClear,
  onSelectAll,
  placeholder,
  menuProps,
  disabled,
  sx,
  searchable,
  forceMultipleLabel,
  isExplicitAll,
}) => {
  const selected = value || [];
  const [search, setSearch] = useState('');
  const showText = () => {
    if (!selected || selected.length === 0) return placeholder || label;
    if (forceMultipleLabel) return 'Multiple';
    // Rely exclusively on parent-provided marker for explicit ALL
    if (isExplicitAll) return 'ALL';
    if (selected.length > 1) return 'Multiple';
    return selected.join(', ');
  };

  const handleAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!entries) return;

    if (onSelectAll) {
      if (isExplicitAll) {
        onClear();
      } else {
        onSelectAll();
      }
      return;
    }

    // Backwards-compatible fallback: toggle by individually selecting all values
    if (selected.length === entries.length) {
      onClear();
    } else {
      const selectedSet = new Set(selected);
      const allValues = entries.map(([, v]) => v);
      allValues.forEach((v) => {
        if (!selectedSet.has(v)) {
          onToggle(v);
        }
      });
    }
  };

  const getFinalList = () => {
    const tokens = (search || '')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const selectedSet = new Set(selected.map((s: any) => String(s)));

    const matchedSelected: Array<[string, string]> = [];
    const matchedUnselected: Array<[string, string]> = [];
    const selectedNotMatched: Array<[string, string]> = [];
    const rest: Array<[string, string]> = [];

    entries.forEach(([key, val]) => {
      const label = (key || '').toString().toLowerCase();
      const v = (val || '').toString();
      const isSelected =
        selectedSet.has(v) || selectedSet.has((key || '').toString());
      const isMatch =
        tokens.length === 0 ? true : tokens.every((t) => label.includes(t));

      if (isMatch && isSelected) matchedSelected.push([key, val]);
      else if (isMatch) matchedUnselected.push([key, val]);
      else if (isSelected) selectedNotMatched.push([key, val]);
      else rest.push([key, val]);
    });

    // When searching, show only matching items (plus already-selected non-matches)
    // so the search behaves as an actual filter.
    if (tokens.length > 0) {
      return [...matchedSelected, ...matchedUnselected, ...selectedNotMatched];
    }

    // Default list (no search): keep original ordering.
    return entries;
  };

  const finalList = getFinalList();

  const defaultMenuProps: any = {
    PaperProps: {
      style: {
        maxHeight: 300,
        zIndex: 40000,
        overflow: 'auto',
        whiteSpace: 'normal',
      },
    },
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    container: typeof window !== 'undefined' ? document.body : undefined,
    getContentAnchorEl: null,
  };

  const finalMenuProps = { ...(defaultMenuProps as any), ...(menuProps || {}) };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          justifyContent: 'space-between',
        }}
      >
        <span className={styles.filterGroupName}>{label}</span>
        <Tooltip title='Clear selection'>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            aria-label={`Clear ${label} selection`}
            sx={{ padding: '4px' }}
          >
            <ClearAllIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>
      <FormControl
        size='small'
        disabled={disabled}
        sx={{ width: '100%', ...sx }}
      >
        <Select
          multiple
          value={selected}
          renderValue={() => {
            if (isExplicitAll) return <span style={{ fontSize: 12 }}>ALL</span>;
            if (!selected || selected.length === 0)
              return <span style={{ fontSize: 12 }}>All</span>;
            if (forceMultipleLabel)
              return <span style={{ fontSize: 12 }}>Multiple</span>;
            if (selected.length > 1)
              return <span style={{ fontSize: 12 }}>Multiple</span>;
            return <span style={{ fontSize: 12 }}>{selected.join(', ')}</span>;
          }}
          displayEmpty
          MenuProps={finalMenuProps}
          sx={{
            '& .MuiSelect-select': {
              paddingTop: '4px',
              paddingBottom: '4px',
              height: 30,
              fontSize: 12,
            },
          }}
        >
          {searchable ? (
            <MenuItem
              disableRipple
              onKeyDown={(e) => e.stopPropagation()} // Prevent closing menu on space
              style={{
                minHeight: 36,
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 1,
              }}
            >
              <input
                type='text'
                placeholder={`Search ${label}...`}
                value={search}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  padding: '6px 8px',
                  fontSize: 12,
                }}
              />
            </MenuItem>
          ) : null}
          <MenuItem
            value='ALL'
            onClick={handleAllClick}
            style={{
              minHeight: 28,
              position: 'sticky',
              top: searchable ? 36 : 0,
              background: 'white',
              zIndex: 1,
            }}
          >
            {/* Use parent-provided explicit ALL flag instead of comparing lengths */}
            <Checkbox
              checked={!!isExplicitAll}
              size='small'
            />
            <em style={{ fontSize: 12 }}>Select All</em>
          </MenuItem>
          {finalList.map(([key, val]) => (
            <MenuItem
              key={key}
              value={val}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggle(val);
              }}
              style={{ minHeight: 28 }}
            >
              <Checkbox
                checked={selected.includes(val)}
                size='small'
              />
              <span style={{ fontSize: 12, whiteSpace: 'normal' }}>{key}</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

const formatMonth = (ym: string) => {
  if (!ym) return 'Select Date';
  const [year, month] = ym.split('_');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getSelectedMonthsDisplay = (startYM?: string, endYM?: string) => {
  if (!startYM || !endYM) return 'None';
  try {
    const [sy, sm] = startYM.split('_').map((s) => parseInt(s, 10));
    const [ey, em] = endYM.split('_').map((s) => parseInt(s, 10));
    const startDate = new Date(sy, sm - 1, 1);
    const endDate = new Date(ey, em - 1, 1);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'None';
    if (endDate < startDate) return 'None';
    return `${formatMonth(startYM)} - ${formatMonth(endYM)}`;
  } catch (e) {
    return 'None';
  }
};

const sanitizeHighlightedHtml = (text: string) =>
  DOMPurify.sanitize(highlightPatterns(text || ''));

const SummaryContent = React.memo(function SummaryContent({
  summaryInfo,
  preview = false,
  reduxTitle,
  isMobile,
  answerRef,
  activeInsightSection,
}: any) {
  const rawMessage = summaryInfo?.chatMessage || '';
  const previewData = useMemo(() => {
    const lines = rawMessage
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    let previewText = '';
    if (lines.length >= 2) {
      previewText = `${lines[0]}\n${lines[1]}`;
    } else if (lines.length === 1) {
      previewText = lines[0];
    } else {
      previewText = rawMessage.slice(0, 300);
    }

    try {
      previewText = previewText.replace(
        /<details[^>]*>[\s\S]*?<\/details>/gi,
        ''
      );
      previewText = previewText.replace(/<summary[^>]*>|<\/summary>/gi, '');
      previewText = previewText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(
          (line: string) =>
            line &&
            !/^\s*(▶|▸|►)?\s*Details[:\s]*$/i.test(line) &&
            !/\bdetails\b/i.test(line)
        )
        .join('\n');
    } catch (e) {}

    return {
      lineCount: lines.length,
      html: sanitizeHighlightedHtml(previewText),
      isEmpty: !previewText.trim(),
    };
  }, [rawMessage]);

  const toplineBlocks = useMemo<
    Array<{ key: number; html: string; isBreak: boolean }>
  >(
    () =>
      rawMessage.split('\n').map((item: string, idx: number) => ({
        key: idx,
        html: sanitizeHighlightedHtml(item),
        isBreak: item === '',
      })),
    [rawMessage]
  );

  if (reduxTitle == 'FLNA Topline') {
    return (
      <div
        className={styles.receivedMessage}
        ref={answerRef}
      >
        <h5
          style={{ textDecoration: 'underline' }}
          hidden={reduxTitle == 'FLNA Topline' ? false : true}
        >
          Frito-Lay Share Topline Insights Summary
        </h5>
        {toplineBlocks.map((block) => (
          <span key={block.key}>
            {block.isBreak ? (
              <br />
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: block.html }}
                className='mt3'
              />
            )}
          </span>
        ))}
        <br />
        {summaryInfo.images ? summaryInfo.images : ''}
        <br />
      </div>
    );
  }

  return (
    <div className={styles.receivedMessage}>
      {preview ? (
        previewData.isEmpty ? (
          <div style={{ minHeight: 80 }}>&nbsp;</div>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: previewData.html }}
            style={
              isMobile
                ? {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    minHeight: 48,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    paddingLeft: 4,
                  }
                : {
                    display: '-webkit-box',
                    WebkitLineClamp:
                      previewData.lineCount >= 3 ? previewData.lineCount : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 12,
                    maxWidth: 'calc(100% - 0px)',
                    wordBreak: 'break-word',
                    paddingLeft: 4,
                  }
            }
          />
        )
      ) : (
        <RenderSummary rawText={summaryInfo} activeInsightSection={activeInsightSection} hideSelectedInsightHeader />
      )}
    </div>
  );
});


const MessageList = ({
  uiMode,
  messages,
  setInputValue,
  setSuggestions,
  handleChange,
  isIdeal,
  onSend,
  title,
  disableSummaryBtn,
  handleSummary2,
  handleSummary,
  tipVisible,
  streamingText,
  allMessages,
  onPinToggle,
  onDeleteMessage,
  activeInsightSection: propActiveInsightSection,
  setActiveInsightSection: propSetActiveInsightSection,
  isSummaryHistoryOpen: propIsSummaryHistoryOpen,
  setIsSummaryHistoryOpen: propSetIsSummaryHistoryOpen,
  isInsightsNavOpen: propIsInsightsNavOpen,
  setIsInsightsNavOpen: propSetIsInsightsNavOpen,
  isFilterDrawerOpen: propIsFilterDrawerOpen,
  setIsFilterDrawerOpen: propSetIsFilterDrawerOpen,
  showFilterHelp: propShowFilterHelp,
  setShowFilterHelp: propSetShowFilterHelp,
  onSummaryToolbarStateChange,
  onSummaryContextChange,
}: MessageListProps) => {
  const formatTimestamp = (ts: any) => {
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(
      d.getMonth() + 1
    )}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const currentUserEmail = sessionStorage.getItem('emailid')?.toLowerCase();
  const selectedMarket = localStorage.getItem('SelectedMarket') || 'UAE MT';
  const dataAvailabilityUrls: Record<
    string,
    { epos: string; merchandizing: string }
  > = {
    'UAE MT': {
      epos: 'https://app.powerbi.com/groups/me/reports/e09c4be9-d6f4-4102-ba8b-5be79fac8cb8/ReportSection9d41bc992866d523b9ec?experience=power-bi&filter=MARKET%2FCountry%20eq%20%27AE02%20-%20UAE%27',
      merchandizing:
        'https://app.powerbi.com/groups/2bfa5d4c-843d-4171-8552-fb2a3727f38a/reports/2806e5c6-2b9b-4230-bb02-3b4fc28c6a75/ReportSection828242b15ed2e8f891fe?experience=power-bi',
    },
    'KSA MT': {
      epos: 'https://app.powerbi.com/groups/02436ce2-421c-4899-b74f-97a4b02035c9/reports/e09c4be9-d6f4-4102-ba8b-5be79fac8cb8/ReportSection9d41bc992866d523b9ec?experience=power-bi&filter=MARKET%2FCountry%20eq%20%27SA02%20-%20SAUDI%27',
      merchandizing:
        'https://app.powerbi.com/groups/2bfa5d4c-843d-4171-8552-fb2a3727f38a/reports/2806e5c6-2b9b-4230-bb02-3b4fc28c6a75/ReportSection828242b15ed2e8f891fe?experience=power-bi',
    },
  };
  const currentDataAvailabilityUrls =
    dataAvailabilityUrls[selectedMarket] || dataAvailabilityUrls['UAE MT'];
  const messagesEndRef = useRef<any>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const [userQuestions, setUserQuestions] = useState<any>([]);
  const [activeLink, setActiveLink] = useState<any>();
  const questionRefs = useRef<any>([]);
  const answerRef = useRef<any>();
  // const answerRef = useRef<(HTMLDivElement | null)[]>([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState<boolean>(false);
  const [currentRecordIndex, setCurrentRecordIndex] = useState<any>();
  const [showSuccessMsg, setShowSuccessMsg] = useState<boolean>(false);
  const [showSavedSuccessMsg, setShowSavedSuccessMsg] = useState<string>('');
  const [allSectionsExpanded, setallSectionsExpanded] = useState(false);
  const [dataRange, setDataRange] = useState({ start: '', end: '' });

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const [activeMenuIndex, setActiveMenuIndex] = React.useState<number | null>(
    null
  );
  const [localShowFilterHelp, setLocalShowFilterHelp] = useState<boolean>(false);
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuIndex(index);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMenuIndex(null);
  };

  const isNearBottom = (container: HTMLElement, threshold = 80) => {
    const remaining =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return remaining <= threshold;
  };

  const getScrollContainer = () => {
    return (
      scrollContainerRef.current ||
      (document.querySelector(`.${styles.scrollableContent}`) as
        | HTMLElement
        | null)
    );
  };

  const scrollToTop = () => {
    const container = getScrollContainer();
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    const container = getScrollContainer();
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    }
  };

  const handleScrollableContainerScroll = () => {
    const container = getScrollContainer();
    if (!container) return;
    shouldStickToBottomRef.current = isNearBottom(container);
  };
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  const [initialFilterLoadDone, setInitialFilterLoadDone] = useState(false);

  const [userInteracted, setUserInteracted] = useState<Record<string, boolean>>(
    () => ({
      category: false,
      region: false,
      sub_region: false,
      retailer: false,
      city: false,
      channel: false,
      retailer_store: false,
      manufacturer: false,
      brand: false,
      sub_brand: false,
      pack_group: false,
      pack_size_group: false,
      price_point: false,
      promo_flag: false,
      subCategory: false,
    })
  );
  const [filters, setFilters] = useState<FiltersState>({
    // comparison: "YTD",
    // year: new Date().getFullYear(), // This will be updated from API data
    // month: 1, // This will be updated from API data
    base_start_month: '',
    base_end_month: '',
    comp_start_month: '', // "comp" for comparison
    comp_end_month: '',
    region: [],
    sub_region: [],
    retailer: [],
    city: [],
    manufacturer: [],
    brand: [],
    sub_brand: [],
    channel: [],
    retailer_store: [],
    promo_flag: [],
    pack_group: [],
    pack_size_group: [],
    price_point: [],
    category: [],
    subCategory: [],
    use_cache: 'true',
  });
  const [summaryIndex, setSummaryIndex] = useState(-1);
  const [isTransitioningView, setIsTransitioningView] = useState(false);
  const [localIsInsightsNavOpen, setLocalIsInsightsNavOpen] = useState(true);
  const [localIsSummaryHistoryOpen, setLocalIsSummaryHistoryOpen] = useState(false);
  const messageRefs = useRef<any[]>([]);
  // remember the index of the most-recent summary the user manually closed
  const lastManualClosedSummaryIndexRef = useRef<number | null>(null);
  // track previous summary count so we only auto-open when a NEW summary arrives
  const prevSummaryCountRef = useRef<number>(0);
  // NEW: Track active insight section in the UAE/KSA inner sidebar
  const [localActiveInsightSection, setLocalActiveInsightSection] = useState<string | null>('KEY TAKEAWAYS');

  // Use props if provided, otherwise use local state
  const activeInsightSection = propActiveInsightSection !== undefined ? propActiveInsightSection : localActiveInsightSection;
  const setActiveInsightSection = propSetActiveInsightSection || setLocalActiveInsightSection;
  const isSummaryHistoryOpen = propIsSummaryHistoryOpen !== undefined ? propIsSummaryHistoryOpen : localIsSummaryHistoryOpen;
  const setIsSummaryHistoryOpen = propSetIsSummaryHistoryOpen || setLocalIsSummaryHistoryOpen;
  const isInsightsNavOpen = propIsInsightsNavOpen !== undefined ? propIsInsightsNavOpen : localIsInsightsNavOpen;
  const setIsInsightsNavOpen = propSetIsInsightsNavOpen || setLocalIsInsightsNavOpen;
  const showFilterHelp = propShowFilterHelp !== undefined ? propShowFilterHelp : localShowFilterHelp;
  const setShowFilterHelp = propSetShowFilterHelp || setLocalShowFilterHelp;

  const [updatedFilters, setUpdatedFilters] = useState<FilterOptions | null>(
    null
  );

  const [scopedAllFlags, setScopedAllFlags] = useState<Record<string, boolean>>(
    {}
  );

  // Map of region -> Set of retailers derived from API combinations so we can scope retailer options
  const [regionRetailerMap, setRegionRetailerMap] = useState<Record<
    string,
    Set<string>
  > | null>(null);

  // Keep raw master arrays returned by the backend so we can compute cross-scoped options
  const [masterGeoData, setMasterGeoData] = useState<any[] | null>(null);
  const [masterProdData, setMasterProdData] = useState<any[] | null>(null);
  const [categoryToSubCategoryMap, setCategoryToSubCategoryMap] = useState<
    Record<string, Set<string>> | null
  >(null);
  const [allSubCategories, setAllSubCategories] = useState<string[]>([]);

  // Ensure SALTY SNACKS is selected by default when filters are updated
  useEffect(() => {
    if (updatedFilters && !filters.subCategory.length) {
      setFilters((prev) => ({
        ...prev,
      }));
    }
  }, [updatedFilters]);

  useEffect(() => {
    if (!categoryToSubCategoryMap) return;

    const selectedCategories = filters.category || [];
    const allowed = new Set<string>();

    if (selectedCategories.length === 0) {
      allSubCategories.forEach((sub) => allowed.add(sub));
    } else {
      selectedCategories.forEach((category) => {
        const subs = categoryToSubCategoryMap[category];
        if (subs) {
          subs.forEach((sub) => allowed.add(sub));
        }
      });
    }

    setFilters((prev) => {
      const current = prev.subCategory || [];
      const next = current.filter((sub) => allowed.has(sub));
      if (next.length === current.length) return prev;
      return { ...prev, subCategory: next };
    });
  }, [filters.category, categoryToSubCategoryMap, allSubCategories]);

  // When updatedFilters loads, set ALL values only on initial load
  useEffect(() => {
    if (!updatedFilters) return;

    // Skip if we've already done the initial load
    if (initialFilterLoadDone) return;

    const labelToKey: Array<[string, keyof FiltersState]> = [
      // Category and Retailer are excluded: UI shows 'All' placeholder without selecting values;
      // payload logic sends all available values when empty.
      ['Region', 'region'],
      ['Sub Region', 'sub_region'],
      ['City', 'city'],
      ['Channel', 'channel'],
      ['Retailer Store', 'retailer_store'],
      ['Promo Flag', 'promo_flag'],
      ['Manufacturer', 'manufacturer'],
      ['Brand', 'brand'],
      ['Sub Brand', 'sub_brand'],
      ['Pack Group', 'pack_group'],
      ['Pack Size', 'pack_size_group'],
      ['Price Point', 'price_point'],
    ];

    let toSet: Partial<FiltersState> = {};
    let changed = false;

    labelToKey.forEach(([label, key]) => {
      try {
        const current = (filters as any)[key] as string[];
        // Only set defaults if the user has explicitly not interacted with this filter
        if (userInteracted[key] === true) return;
        // Don't override existing selections
        if (current && current.length > 0) return;
        const opts = (updatedFilters as any)[label as string];
        if (!opts) return;
        const vals = Object.values(opts).filter(
          (v) => typeof v === 'string' && (v as string).toUpperCase() !== 'ALL'
        ) as string[];
        if (vals.length) {
          (toSet as any)[key] = vals;
          changed = true;
        }
      } catch (e) {
        // ignore
      }
    });

    if (changed) {
      setFilters((prev) => ({ ...prev, ...toSet }));
      // Mark that we've done the initial filter load
      setInitialFilterLoadDone(true);
    }
  }, [updatedFilters, userInteracted, initialFilterLoadDone]);

  const [syncFilters, setSyncFilters] = useState(true);
  const [availableMonthsByYear, setAvailableMonthsByYear] = useState<
    Record<number, number[]>
  >({});
  const [localIsFilterDrawerOpen, setLocalIsFilterDrawerOpen] = useState(false);
  const isFilterDrawerOpen = propIsFilterDrawerOpen !== undefined ? propIsFilterDrawerOpen : localIsFilterDrawerOpen;
  const setIsFilterDrawerOpen = propSetIsFilterDrawerOpen || setLocalIsFilterDrawerOpen;
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Helper function to increment month
  const incrementMonth = (yearMonth: string): string => {
    if (!yearMonth) return '';
    const [yearStr, monthStr] = yearMonth.split('_');
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10);

    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }

    const monthPadded = month.toString().padStart(2, '0');
    return `${year}_${monthPadded}`;
  };

  // When user clicks 'Apply Filters' after selecting period/region, call backend to get scoped filters
  const handleApplyFilters = async () => {
    const params: any = {};
    const toNonEmptyValues = (values: any): string[] =>
      Array.isArray(values)
        ? values
            .map((v) => String(v || '').trim())
            .filter((v) => v.length > 0)
        : [];

    const selectedCategories = toNonEmptyValues(filters.category);
    const selectedSubCategories = toNonEmptyValues(filters.subCategory);
    const selectedRegions = toNonEmptyValues(filters.region);

    if (filters.base_start_month) params.base_start = filters.base_start_month;
    if (filters.base_end_month) {
      params.base_end = incrementMonth(filters.base_end_month);
    }
    if (filters.comp_start_month) params.comp_start = filters.comp_start_month;
    if (filters.comp_end_month) {
      params.comp_end = incrementMonth(filters.comp_end_month);
    }
    // For UAE filters endpoint, values must be semicolon-separated.
    // When category is empty (default "All" placeholder), send all available category values.
    if (selectedCategories.length) {
      params.category = selectedCategories.join(';');
    } else {
      const catOpts = updatedFilters && (updatedFilters as any)['Category']
        ? (updatedFilters as any)['Category']
        : (filterOptions as any)['Category'];
      const allCatValues = catOpts && typeof catOpts === 'object'
        ? (Object.values(catOpts).filter(
            (v: any) => typeof v === 'string' && String(v).toUpperCase() !== 'ALL'
          ) as string[])
        : [];
      params.category = allCatValues.join(';');
    }
    // Do not send sub_category when there is no actual selected value.
    if (selectedSubCategories.length) {
      // When ALL is explicitly selected, send "ALL" instead of all individual values
      if (scopedAllFlags && scopedAllFlags['subCategory']) {
        params.sub_category = 'ALL';
      } else {
        params.sub_category = selectedSubCategories.join(';');
      }
    }
    // Keep region in params as optional (in case backend needs it)
    if (selectedRegions.length) {
      params.region = selectedRegions.join(';');
    }

    setIsApplyingFilters(true);
    try {
      // Retry once on transient failure: attempt up to 2 times
      let resp: any = null;
      let lastErr: any = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          resp = await getFiltersBySelection(params);
          break; // success
        } catch (e) {
          lastErr = e;
          // if this was the first attempt, retry once; otherwise rethrow
          if (attempt === 0) continue;
          throw e;
        }
      }

      if (resp) {
        // Determine the updated filters object explicitly so we can log it for debugging
        try {
          const toOptionObject = (vals: any) => {
            if (!vals) return vals;
            // case-insensitive comparator for stable alphabetical ordering
            const ciSort = (a: string, b: string) =>
              String(a).toLowerCase().localeCompare(String(b).toLowerCase());

            if (Array.isArray(vals)) {
              const sorted = [...vals].sort(ciSort);
              return {
                ALL: 'ALL',
                ...Object.fromEntries(sorted.map((v: any) => [v, v])),
              };
            }
            if (typeof vals === 'object') {
              const entries = Object.entries(vals).sort((a, b) =>
                String(a[0])
                  .toLowerCase()
                  .localeCompare(String(b[0]).toLowerCase())
              );
              return Object.fromEntries(entries);
            }

            return vals;
          };

          const updated: any = { ...(filterOptions as any) };
          // Preserve previously loaded category/sub-category options unless the
          // current response provides replacements in the expected format.
          if (updatedFilters && (updatedFilters as any)['Category']) {
            updated['Category'] = (updatedFilters as any)['Category'];
          }
          if (updatedFilters && (updatedFilters as any)['Sub Category']) {
            updated['Sub Category'] = (updatedFilters as any)['Sub Category'];
          }

          setUserInteracted(() => ({
            category: false,
            region: false,
            retailer: false,
            channel: false,
            retailer_store: false,
            manufacturer: false,
            brand: false,
            sub_brand: false,
            pack_group: false,
            pack_size_group: false,
            price_point: false,
            promo_flag: false,
            subCategory: false,
          }));
          setInitialFilterLoadDone(false);

          if (resp.combinations && Array.isArray(resp.combinations)) {
            const regionsSet = new Set<string>();
            const retailersSet = new Set<string>();
            const map: Record<string, Set<string>> = {};
            resp.combinations.forEach((c: any) => {
              if (c.region) {
                regionsSet.add(c.region);
                if (!map[c.region]) map[c.region] = new Set<string>();
                if (c.retailer) {
                  map[c.region].add(c.retailer);
                  retailersSet.add(c.retailer);
                }
              } else if (c.retailer) {
                retailersSet.add(c.retailer);
              }
            });
            const regions = Array.from(regionsSet);
            const retailers = Array.from(retailersSet);
            updated['Region'] = toOptionObject(regions);
            setRegionRetailerMap(map);
          } else if (resp.combinations && typeof resp.combinations === 'object' && !Array.isArray(resp.combinations)) {
            // combinations is an object like { retailer: [...], region: [...], ... }
            const combObj = resp.combinations as Record<string, any>;
            if (Array.isArray(combObj.retailer)) {
              updated['Retailer'] = toOptionObject(combObj.retailer);
            }
            if (Array.isArray(combObj.region)) {
              updated['Region'] = toOptionObject(combObj.region);
            }
            if (Array.isArray(combObj.sub_region)) {
              updated['Sub Region'] = toOptionObject(combObj.sub_region);
            }
            if (Array.isArray(combObj.city)) {
              updated['City'] = toOptionObject(combObj.city);
            }
            if (Array.isArray(combObj.channel)) {
              updated['Channel'] = toOptionObject(combObj.channel);
            }
            if (Array.isArray(combObj.retailer_store) || Array.isArray(combObj.retailer_store_name)) {
              updated['Retailer Store'] = toOptionObject(combObj.retailer_store || combObj.retailer_store_name);
            }
            if (Array.isArray(combObj.promo_flag)) {
              updated['Promo Flag'] = toOptionObject(combObj.promo_flag);
            }
            if (Array.isArray(combObj.manufacturer)) {
              updated['Manufacturer'] = toOptionObject(combObj.manufacturer);
            }
            if (Array.isArray(combObj.brand)) {
              updated['Brand'] = toOptionObject(combObj.brand);
            }
            if (Array.isArray(combObj.sub_brand)) {
              updated['Sub Brand'] = toOptionObject(combObj.sub_brand);
            }
            if (Array.isArray(combObj.pack_group)) {
              updated['Pack Group'] = toOptionObject(combObj.pack_group);
            }
            if (Array.isArray(combObj.pack_size_group)) {
              updated['Pack Size'] = toOptionObject(combObj.pack_size_group);
            }
            if (Array.isArray(combObj.price_point)) {
              updated['Price Point'] = toOptionObject(combObj.price_point);
            }
          } else if (resp.geo && Array.isArray(resp.geo)) {
            const safe = (v: any) => {
              if (v === null || typeof v === 'undefined') return null;
              if (typeof v === 'number') return String(v);
              if (typeof v === 'string') return v.trim();
              return String(v);
            };

            const regionsSet = new Set<string>();
            const subRegionSet = new Set<string>();
            const retailersSet = new Set<string>();
            const citySet = new Set<string>();
            const channelsSet = new Set<string>();
            const retailerStoreSet = new Set<string>();
            const promoFlagSet = new Set<string>();
            const map: Record<string, Set<string>> = {};

            resp.geo.forEach((item: any) => {
              const region = safe(item.region);
              const subRegion = safe(item.sub_region);
              const retailer = safe(item.retailer);
              const city = safe(item.city);
              const channel = safe(item.channel);
              const rstore = safe(item.retailer_store_name);
              const promoFlag = safe(item.promo_flag);

              if (region) {
                regionsSet.add(region);
                if (!map[region]) map[region] = new Set<string>();
                if (retailer) map[region].add(retailer);
              }
              if (subRegion) subRegionSet.add(subRegion);
              if (retailer) retailersSet.add(retailer);
              if (city) citySet.add(city);
              if (channel) channelsSet.add(channel);
              if (rstore) retailerStoreSet.add(rstore);
              if (promoFlag) promoFlagSet.add(promoFlag);
            });

            const regions = Array.from(regionsSet);
            const subRegions = Array.from(subRegionSet);
            const retailers = Array.from(retailersSet);
            const cities = Array.from(citySet);
            const channels = Array.from(channelsSet);
            const retailerStores = Array.from(retailerStoreSet);
            const promoFlags = Array.from(promoFlagSet);

            updated['Region'] = toOptionObject(regions);
            updated['Sub Region'] = toOptionObject(subRegions);
            updated['Retailer'] = toOptionObject(retailers);
            updated['City'] = toOptionObject(cities);
            updated['Channel'] = toOptionObject(channels);
            updated['Retailer Store'] = toOptionObject(retailerStores);
            updated['Promo Flag'] = toOptionObject(promoFlags);
            setRegionRetailerMap(map);
            setMasterGeoData(resp.geo);
          } else {
            setRegionRetailerMap(null);
            if (resp.Region) updated['Region'] = toOptionObject(resp.Region);
            if (resp['Sub Region'])
              updated['Sub Region'] = toOptionObject(resp['Sub Region']);
            if (resp.Retailer)
              updated['Retailer'] = toOptionObject(resp.Retailer);
            if (resp.City) updated['City'] = toOptionObject(resp.City);
            if (resp.promo_flag)
              updated['Promo Flag'] = toOptionObject(resp.promo_flag);
          }

          if (resp.prod && Array.isArray(resp.prod)) {
            const safe = (v: any) => {
              if (v === null || typeof v === 'undefined') return null;
              if (typeof v === 'number') return String(v);
              if (typeof v === 'string') return v.trim();
              return String(v);
            };

            const brands = Array.from(
              new Set(resp.prod.map((p: any) => safe(p.brand)).filter(Boolean))
            );
            const subBrands = Array.from(
              new Set(
                resp.prod.map((p: any) => safe(p.sub_brand)).filter(Boolean)
              )
            );
            const manufacturers = Array.from(
              new Set(
                resp.prod.map((p: any) => safe(p.manufacturer)).filter(Boolean)
              )
            );
            const packGroups = Array.from(
              new Set(
                resp.prod.map((p: any) => safe(p.pack_group)).filter(Boolean)
              )
            );
            const packSizes = Array.from(
              new Set(
                resp.prod.map((p: any) => safe(p.pack_size_group)).filter(Boolean)
              )
            );
            const pricePoints = Array.from(
              new Set(
                resp.prod.map((p: any) => safe(p.price_point)).filter(Boolean)
              )
            );

            updated['Brand'] = toOptionObject(brands);
            updated['Sub Brand'] = toOptionObject(subBrands);
            updated['Manufacturer'] = toOptionObject(manufacturers);
            updated['Pack Group'] = toOptionObject(packGroups);
            updated['Pack Size'] = toOptionObject(packSizes);
            updated['Price Point'] = toOptionObject(pricePoints);
            setMasterProdData(resp.prod);
          }

          if (resp['Sub Category'])
            updated['Sub Category'] = toOptionObject(resp['Sub Category']);

          let hasCategoryCombinationData = false;
          if (
            resp.category_combinations &&
            Array.isArray(resp.category_combinations)
          ) {
            hasCategoryCombinationData = true;
            const categoriesSet = new Set<string>();
            const subCategoriesSet = new Set<string>();
            const categoryMap: Record<string, Set<string>> = {};
            resp.category_combinations.forEach((combo: any) => {
              const category = combo?.category ? String(combo.category) : '';
              const subCategory = combo?.sub_category
                ? String(combo.sub_category)
                : '';

              if (category) categoriesSet.add(category);
              if (subCategory) subCategoriesSet.add(subCategory);

              if (category && subCategory) {
                if (!categoryMap[category]) categoryMap[category] = new Set();
                categoryMap[category].add(subCategory);
              }
            });
            if (categoriesSet.size) {
              updated['Category'] = toOptionObject(Array.from(categoriesSet));
            }
            if (subCategoriesSet.size) {
              updated['Sub Category'] = toOptionObject(
                Array.from(subCategoriesSet)
              );
            }
            setCategoryToSubCategoryMap(categoryMap);
            setAllSubCategories(Array.from(subCategoriesSet));
          }

          // If current response doesn't include category combination shape,
          // keep the previously built dependency map/lists intact.
          if (!hasCategoryCombinationData) {
            // no-op intentionally; state remains from initial response
          }

          setUpdatedFilters(updated);
          setInitialFilterLoadDone(true);
          setSyncFilters(false);
          setFilters((prev) => ({
            ...prev,
            region: [],
            sub_region: [],
            retailer: [],
            city: [],
            manufacturer: [],
            brand: [],
            sub_brand: [],
            channel: [],
            retailer_store: [],
            promo_flag: [],
            pack_group: [],
            pack_size_group: [],
            price_point: [],
          }));
        } catch (e) {
          console.error('Error processing apply filters response:', e);
        }

        setFilters((prev) => ({
          ...prev,
          region: [],
          sub_region: [],
          retailer: [],
          city: [],
        }));

        setFiltersApplied(true);
      }
    } catch (err) {
      console.error('Apply filters failed', err);
      try {
        const fallback: any = {
          ...(filterOptions as any),
          use_cache: { false: 'false', true: 'true' },
        };
        if (updatedFilters && (updatedFilters as any)['Category']) {
          fallback['Category'] = (updatedFilters as any)['Category'];
        }
        if (updatedFilters && (updatedFilters as any)['Sub Category']) {
          fallback['Sub Category'] = (updatedFilters as any)['Sub Category'];
        }
        setUpdatedFilters(fallback);

        // Map fallback categories to internal filter keys and select all options
        const mapCatToFilterKey: Record<
          string,
          keyof FiltersState | 'subCategory'
        > = {
          Category: 'category',
          Region: 'region',
          'Sub Region': 'sub_region',
          Retailer: 'retailer',
          City: 'city',
          Channel: 'channel',
          'Retailer Store': 'retailer_store',
          Brand: 'brand',
          'Sub Brand': 'sub_brand',
          Manufacturer: 'manufacturer',
          'Pack Group': 'pack_group',
          'Pack Size': 'pack_size_group',
          'Price Point': 'price_point',
          'Promo Flag': 'promo_flag',
          'Sub Category': 'subCategory',
        };

        const newFilters: any = { ...filters };
        Object.entries(mapCatToFilterKey).forEach(([cat, fk]) => {
          const opts = fallback[cat];
          if (opts && typeof opts === 'object') {
            const values = Object.keys(opts).filter(
              (k) => k.toUpperCase() !== 'ALL'
            );
            newFilters[fk as any] = values;
          }
        });

        setFilters(newFilters as FiltersState);

        setFiltersApplied(true);
        setSyncFilters(false);
      } catch (e) {
        console.error('Failed to apply fallback filters on API error', e);
      }
    } finally {
      setIsApplyingFilters(false);
    }
  };
  const handleFilterChange = (name: string, value: string) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeEvent = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setFilters((prevState) => {
      const newFilters = { ...prevState };

      switch (name) {
        // case "year":
        //   const year = Number(value);
        //   newFilters.year = year;

        //   // Reset month to first available month for selected year
        //   const availableMonths = availableMonthsByYear[year] || [];
        //   const currentMonthNum = Number(prevState.month);

        //   if (!availableMonths.includes(currentMonthNum)) {
        //     // Reset month to first available month if current month is invalid
        //     const firstAvailableMonth = availableMonths[0];
        //     newFilters.month = firstAvailableMonth || 1; // Default to 1 if no months available
        //   }
        //   break;

        // case "month":
        //   newFilters.month = Number(value);
        //   break;

        // case "comparison":
        //   newFilters.comparison = value;
        //   break;

        // case "region":
        //   newFilters.region = value;
        //   break;

        // case "retailer":
        //   newFilters.retailer = value;
        //   break;

        // case "subCategory":
        //   newFilters.subCategory = value;
        //   break;

        case 'use_cache':
          newFilters.use_cache = value;
          break;
      }

      return newFilters;
    });
  };
  const handleMultiSelectChange = (
    filterKey: keyof FiltersState,
    value: string
  ) => {
    setFilters((prev) => {
      const currentSelection = (prev[filterKey] as string[]) || [];
      const newSelection = currentSelection.includes(value)
        ? currentSelection.filter((item) => item !== value) // Deselect
        : [...currentSelection, value]; // Select
      return { ...prev, [filterKey]: newSelection };
    });
  };

  const handleClearSelection = (filterKey: keyof FiltersState) => {
    setUserInteracted((prev) => ({
      ...prev,
      [filterKey]: true,
    }));
    setFilters((prev) => ({
      ...prev,
      [filterKey]: [],
    }));
    setScopedAllFlags((prev) => ({ ...prev, [filterKey as string]: false }));
  };

  const getLabelForFilterKey = (fk: keyof FiltersState) => {
    const map: Record<string, string> = {
      category: 'Category',
      region: 'Region',
      sub_region: 'Sub Region',
      retailer: 'Retailer',
      city: 'City',
      channel: 'Channel',
      retailer_store: 'Retailer Store',
      manufacturer: 'Manufacturer',
      brand: 'Brand',
      sub_brand: 'Sub Brand',
      pack_group: 'Pack Group',
      pack_size_group: 'Pack Size',
      price_point: 'Price Point',
      subCategory: 'Sub Category',
      promo_flag: 'Promo Flag',
    };
    return map[String(fk)] || String(fk);
  };

  const getAvailableValuesForKey = (fk: keyof FiltersState) => {
    if (fk === 'subCategory' && categoryToSubCategoryMap) {
      const selectedCategories = filters.category || [];
      if (!selectedCategories.length) return allSubCategories;

      const allowed = new Set<string>();
      selectedCategories.forEach((category) => {
        const subSet = categoryToSubCategoryMap[category];
        if (subSet) subSet.forEach((sub) => allowed.add(sub));
      });
      return Array.from(allowed);
    }

    const label = getLabelForFilterKey(fk);
    const opts =
      updatedFilters && (updatedFilters as any)[label]
        ? (updatedFilters as any)[label]
        : (filterOptions as any)[label];
    if (!opts || typeof opts !== 'object') return [] as string[];
    return Object.values(opts).filter(
      (v) => typeof v === 'string' && (v as string).toUpperCase() !== 'ALL'
    ) as string[];
  };

  const handleToggleOption = (filterKey: keyof FiltersState, value: string) => {
    setUserInteracted((prev) => ({ ...prev, [filterKey as string]: true }));
    setFilters((prev) => {
      const current = (prev as any)[filterKey] || [];
      const exists = current.includes(value);
      const newSel = exists
        ? current.filter((it: string) => it !== value)
        : [...current, value];

      // Determine whether the user now has selected every available option.
      const available = getAvailableValuesForKey(filterKey);
      if (available.length && newSel.length === available.length) {
        setScopedAllFlags((prev) => ({ ...prev, [filterKey as string]: true }));
      } else {
        setScopedAllFlags((prev) => ({
          ...prev,
          [filterKey as string]: false,
        }));
      }

      return { ...prev, [filterKey]: newSel };
    });
  };

  const handleSelectAll = (
    filterKey: keyof FiltersState,
    allOptions: FilterGroup
  ) => {
    const allValues = Object.values(allOptions).filter(
      (v) => typeof v === 'string' && v.toUpperCase() !== 'ALL'
    );

    // Check if all options are currently selected
    const areAllSelected =
      filters[filterKey]?.length === allValues.length &&
      allValues.every((value) => filters[filterKey].includes(value));

    // Toggle between all selected and none selected
    if (areAllSelected) {
      // Clear all selections
      setFilters((prev: FiltersState) => ({ ...prev, [filterKey]: [] }));
      setScopedAllFlags((prev) => ({ ...prev, [filterKey as string]: false }));
    } else {
      // Select all
      setFilters((prev: FiltersState) => ({
        ...prev,
        [filterKey]: [...allValues],
      }));
      setScopedAllFlags((prev) => ({ ...prev, [filterKey as string]: true }));
    }

    // Mark that the user explicitly interacted with this filter
    setUserInteracted((prev) => ({ ...prev, [filterKey as string]: true }));
  };

  let dispatch = useDispatch();
  let reduxTitle = useAppSelector((state) => state.chatBotDetails.title);
  const isMiddleEastMarket =
    reduxTitle === 'UAE MT' || reduxTitle === 'KSA MT';

  // 	const handleSetIsPrinting = useCallback((value: boolean) => {
  //     setIsPrinting(value);
  //   }, []);
  // 	useEffect(() => {
  //     console.log('State in MessageList changed. allExpanded is now:', allExpanded);
  //   }, [allExpanded]);
  // We no longer use a popover; render inline month pickers.
  // Keep a simple active state if needed for styling (not required).
  const [activePicker, setActivePicker] = useState<'base' | 'comp' | null>(
    null
  );
  const [regionSearch, setRegionSearch] = useState('');
  const [retailerSearch, setRetailerSearch] = useState('');
  // productSearch holds per-product search strings keyed by the product label (e.g. 'Brand', 'Pack Size')
  const [productSearch, setProductSearch] = useState<Record<string, string>>(
    {}
  );
  useEffect(() => {
    if (title != '' && title != null) {
      dispatch(updateTitle(title));
    }
  }, [title]);

  useEffect(() => {
    const messageList = messages.map((item: any, index: number) => ({
      row_number: index,
      ...item,
    }));

    // consider only rendered messages (exclude _hidden / deleted) when auto-opening summaries
    const rendered = (messages || []).filter(
      (m: any) => !(m?.sender === '_hidden' || m?.is_deleted)
    );
    const lastMsg =
      rendered && rendered.length ? rendered[rendered.length - 1] : null;
    if (lastMsg && lastMsg.sender === 'summary' && isMiddleEastMarket) {
      // Count current summaries
      const currentSummaryCount = rendered.filter((m: any) => m?.sender === 'summary').length;
      const newestIdx = rendered.length - 1;
      // Only auto-open if a genuinely NEW summary was added (count increased)
      if (currentSummaryCount > prevSummaryCountRef.current) {
        if (lastManualClosedSummaryIndexRef.current === newestIdx) {
          // user recently closed this newest summary; respect their choice and do not auto-open
        } else {
          setSummaryIndex(newestIdx);
        }
        // Reset to KEY TAKEAWAYS tab when new response arrives
        setActiveInsightSection('KEY TAKEAWAYS');
      }
      prevSummaryCountRef.current = currentSummaryCount;
    }

    setUserQuestions(
      messageList?.filter((msg: any, index: number) => msg?.sender === 123)
    );
  }, [messages]);

  useLayoutEffect(() => {
    if (uiMode !== 'chat') return;
    // Keep chat pinned only when user is already near the bottom.
    if (!shouldStickToBottomRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      scrollToBottom();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [messages, uiMode]);

  useLayoutEffect(() => {
    if (uiMode !== 'chat') return;

    // Always scroll to bottom when tips visibility changes so action buttons remain visible
    const frame = window.requestAnimationFrame(() => {
      scrollToBottom();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [tipVisible, uiMode]);

  useLayoutEffect(() => {
    if (uiMode !== 'chat') return;
    if (!shouldStickToBottomRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      scrollToBottom();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
    // Depend only on messages length + isIdeal, not on the streaming text string
    // so we don't fire an expensive layout effect on every character update.
  }, [messages, isIdeal, uiMode]);

  useEffect(() => {
    if (summaryIndex >= 0 && messageRefs.current[summaryIndex]) {
      try {
        messageRefs.current[summaryIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } catch (e) {
        // ignore
      }
    }
  }, [summaryIndex]);

  const scrollToQuestion = (index: number, message: string) => {
    setActiveLink(index);
    questionRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    callLogEvent(
      'CLICKED_ON_NEW_DAILOG_MESSAGE_HISTORY',
      'clicked on one of the recent messages: ' + message
    );
  };

  const setLatestSuggestions = (
    question: string,
    currentSuggestions: string[]
  ) => {
    setInputValue(question);
    setSuggestions(
      currentSuggestions.filter((suggestion) => suggestion !== question)
    );
  };
  const getSummaryStyles = (summaryInfo: any, preview: boolean = false, activeInsightSection?: string | null) => {
    return (
      <SummaryContent
        summaryInfo={summaryInfo}
        preview={preview}
        reduxTitle={reduxTitle}
        isMobile={isMobile}
        answerRef={answerRef}
        activeInsightSection={activeInsightSection}
      />
    );
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        // const data = await getFiltersApi();
        const data = (await getFiltersApi()) as ApiResponse;
        if (data?.year_month && Array.isArray(data.year_month)) {
          const sorted = [...data.year_month].sort();
          setAvailableMonths(data.year_month);
          setDataRange({ start: sorted[0], end: sorted[sorted.length - 1] });
          setFilters((prev) => ({
            ...prev,
            start_month: sorted[0],
            end_month: sorted[sorted.length - 1],
          }));
        }
      } catch (err) {
        console.error('Filter API failed', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (syncFilters) {
      setUpdatedFilters({
        ...filterOptions,
        use_cache: {
          false: 'false',
          true: 'true',
        },
      });

      const fetchDataAsync = async () => {
        try {
          const data = (await getFiltersApi()) as ApiResponse;

          // Process year_month data
          // if (data.year_month && Array.isArray(data.year_month)) {
          //   const yearMonthMap: Record<number, number[]> = {};
          //   const monthNames = [
          //     "January",
          //     "February",
          //     "March",
          //     "April",
          //     "May",
          //     "June",
          //     "July",
          //     "August",
          //     "September",
          //     "October",
          //     "November",
          //     "December",
          //   ];

          // Build year to months mapping
          // console.log("Processing year_month data:", data.year_month);

          // data.year_month.forEach((yearMonth: string) => {
          //   const [yearStr, monthStr] = yearMonth.split("_");
          //   const year = parseInt(yearStr);
          //   const month = parseInt(monthStr);

          //   if (!yearMonthMap[year]) {
          //     yearMonthMap[year] = [];
          //   }
          //   if (!yearMonthMap[year].includes(month)) {
          //     yearMonthMap[year].push(month);
          //   }
          // });

          // // Sort months within each year
          // Object.keys(yearMonthMap).forEach((yearStr) => {
          //   const year = parseInt(yearStr);
          //   yearMonthMap[year].sort((a, b) => a - b);
          // });

          // console.log("Year to months mapping:", yearMonthMap);
          // setAvailableMonthsByYear(yearMonthMap);

          // Update filters immediately if current year/month is invalid
          //   setFilters((prevFilters) => {
          //     const currentYear = prevFilters.year;
          //     const currentMonth = prevFilters.month;

          //     if (
          //       !yearMonthMap[currentYear] ||
          //       !yearMonthMap[currentYear].includes(currentMonth)
          //     ) {
          //       // Get the most recent year
          //       const availableYears = Object.keys(yearMonthMap).map(Number);
          //       const latestYear = Math.max(...availableYears);
          //       const availableMonths = yearMonthMap[latestYear] || [];

          //       return {
          //         ...prevFilters,
          //         year: latestYear,
          //         month: availableMonths[0] || 1,
          //       };
          //     }

          //     return prevFilters;
          //   });
          // }

          setUpdatedFilters((prev) => {
            if (!prev || typeof prev !== 'object') return prev;

            const updated: FilterOptions = { ...prev };

            if (data.geo && Array.isArray(data.geo)) {
              const safe = (v: any) => {
                if (v === null || typeof v === 'undefined') return null;
                if (typeof v === 'number') return String(v);
                if (typeof v === 'string') return v.trim();
                return String(v);
              };

              const regions = Array.from(
                new Set(
                  data.geo.map((item: any) => safe(item.region)).filter(Boolean)
                )
              );
              const subRegions = Array.from(
                new Set(
                  data.geo
                    .map((item: any) => safe(item.sub_region))
                    .filter(Boolean)
                )
              );
              const retailers = Array.from(
                new Set(
                  data.geo
                    .map((item: any) => safe(item.retailer))
                    .filter(Boolean)
                )
              );
              const cities = Array.from(
                new Set(
                  data.geo.map((item: any) => safe(item.city)).filter(Boolean)
                )
              );
              const channels = Array.from(
                new Set(
                  data.geo
                    .map((item: any) => safe(item.channel))
                    .filter(Boolean)
                )
              );
              const retailerStores = Array.from(
                new Set(
                  data.geo
                    .map((item: any) => safe(item.retailer_store_name))
                    .filter(Boolean)
                )
              );
              const promoFlags = Array.from(
                new Set(
                  data.geo
                    .map((item: any) => safe(item.promo_flag))
                    .filter(Boolean)
                )
              );

              updated['Region'] = {
                ALL: 'ALL',
                ...Object.fromEntries(regions.map((val: any) => [val, val])),
              };
              updated['Sub Region'] = {
                ALL: 'ALL',
                ...Object.fromEntries(subRegions.map((val: any) => [val, val])),
              };
              updated['Retailer'] = {
                ALL: 'ALL',
                ...Object.fromEntries(retailers.map((val: any) => [val, val])),
              };
              updated['City'] = {
                ALL: 'ALL',
                ...Object.fromEntries(cities.map((val: any) => [val, val])),
              };
              updated['Channel'] = {
                ALL: 'ALL',
                ...Object.fromEntries(channels.map((val: any) => [val, val])),
              };
              updated['Retailer Store'] = {
                ALL: 'ALL',
                ...Object.fromEntries(
                  retailerStores.map((val: any) => [val, val])
                ),
              };
              updated['Promo Flag'] = {
                ALL: 'ALL',
                ...Object.fromEntries(promoFlags.map((val: any) => [val, val])),
              };
              // persist master geo rows for cross-scoping logic
              setMasterGeoData(data.geo);
            }

            if (data.prod && Array.isArray(data.prod)) {
              const safe = (v: any) => {
                if (v === null || typeof v === 'undefined') return null;
                if (typeof v === 'number') return String(v);
                if (typeof v === 'string') return v.trim();
                return String(v);
              };

              const brands = Array.from(
                new Set(
                  data.prod.map((item: any) => safe(item.brand)).filter(Boolean)
                )
              );
              const subBrands = Array.from(
                new Set(
                  data.prod
                    .map((item: any) => safe(item.sub_brand))
                    .filter(Boolean)
                )
              );
              const manufacturers = Array.from(
                new Set(
                  data.prod
                    .map((item: any) => safe(item.manufacturer))
                    .filter(Boolean)
                )
              );
              const packGroups = Array.from(
                new Set(
                  data.prod
                    .map((item: any) => safe(item.pack_group))
                    .filter(Boolean)
                )
              );
              const packSizes = Array.from(
                new Set(
                  data.prod
                    .map((item: any) => safe(item.pack_size_group))
                    .filter(Boolean)
                )
              );
              const pricePoints = Array.from(
                new Set(
                  data.prod
                    .map((item: any) => safe(item.price_point))
                    .filter(Boolean)
                )
              );

              updated['Brand'] = {
                ALL: 'ALL',
                ...Object.fromEntries(brands.map((val: any) => [val, val])),
              };
              updated['Sub Brand'] = {
                ALL: 'ALL',
                ...Object.fromEntries(subBrands.map((val: any) => [val, val])),
              };
              updated['Manufacturer'] = {
                ALL: 'ALL',
                ...Object.fromEntries(
                  manufacturers.map((val: any) => [val, val])
                ),
              };
              updated['Pack Group'] = {
                ALL: 'ALL',
                ...Object.fromEntries(packGroups.map((val: any) => [val, val])),
              };
              updated['Pack Size'] = {
                ALL: 'ALL',
                ...Object.fromEntries(packSizes.map((val: any) => [val, val])),
              };
              updated['Price Point'] = {
                ALL: 'ALL',
                ...Object.fromEntries(
                  pricePoints.map((val: any) => [val, val])
                ),
              };
              // persist master product rows for cross-scoping logic
              setMasterProdData(data.prod);
            }

            // Handle combinations as an object { retailer: [...], region: [...], ... }
            if (data.combinations && typeof data.combinations === 'object' && !Array.isArray(data.combinations)) {
              const combObj = data.combinations as Record<string, any>;
              const toOpt = (arr: string[]) => ({
                ALL: 'ALL',
                ...Object.fromEntries(arr.map((val: string) => [val, val])),
              });
              if (Array.isArray(combObj.retailer)) updated['Retailer'] = toOpt(combObj.retailer);
              if (Array.isArray(combObj.region)) updated['Region'] = toOpt(combObj.region);
              if (Array.isArray(combObj.sub_region)) updated['Sub Region'] = toOpt(combObj.sub_region);
              if (Array.isArray(combObj.city)) updated['City'] = toOpt(combObj.city);
              if (Array.isArray(combObj.channel)) updated['Channel'] = toOpt(combObj.channel);
              if (Array.isArray(combObj.retailer_store) || Array.isArray(combObj.retailer_store_name))
                updated['Retailer Store'] = toOpt(combObj.retailer_store || combObj.retailer_store_name);
              if (Array.isArray(combObj.promo_flag)) updated['Promo Flag'] = toOpt(combObj.promo_flag);
              if (Array.isArray(combObj.manufacturer)) updated['Manufacturer'] = toOpt(combObj.manufacturer);
              if (Array.isArray(combObj.brand)) updated['Brand'] = toOpt(combObj.brand);
              if (Array.isArray(combObj.sub_brand)) updated['Sub Brand'] = toOpt(combObj.sub_brand);
              if (Array.isArray(combObj.pack_group)) updated['Pack Group'] = toOpt(combObj.pack_group);
              if (Array.isArray(combObj.pack_size_group)) updated['Pack Size'] = toOpt(combObj.pack_size_group);
              if (Array.isArray(combObj.price_point)) updated['Price Point'] = toOpt(combObj.price_point);
            }

            Object.entries(data).forEach(([incomingKey, values]) => {
              if (incomingKey === 'combinations') return; // already handled above
              if (incomingKey.toLowerCase() === 'promo_flag' && Array.isArray(values)) {
                updated['Promo Flag'] = {
                  ALL: 'ALL',
                  ...Object.fromEntries(values.map((val) => [val, val])),
                };
                return;
              }

              if (
                incomingKey === 'category_combinations' &&
                Array.isArray(values)
              ) {
                const categoriesSet = new Set<string>();
                const subCategoriesSet = new Set<string>();
                const categoryMap: Record<string, Set<string>> = {};
                values.forEach((combo: any) => {
                  const category = combo?.category ? String(combo.category) : '';
                  const subCategory = combo?.sub_category
                    ? String(combo.sub_category)
                    : '';

                  if (category) categoriesSet.add(category);
                  if (subCategory) subCategoriesSet.add(subCategory);

                  if (category && subCategory) {
                    if (!categoryMap[category]) categoryMap[category] = new Set();
                    categoryMap[category].add(subCategory);
                  }
                });

                if (categoriesSet.size) {
                  updated['Category'] = {
                    ALL: 'ALL',
                    ...Object.fromEntries(
                      Array.from(categoriesSet).map((val) => [val, val])
                    ),
                  };
                }

                if (subCategoriesSet.size) {
                  updated['Sub Category'] = {
                    ALL: 'ALL',
                    ...Object.fromEntries(
                      Array.from(subCategoriesSet).map((val) => [val, val])
                    ),
                  };
                }
                setCategoryToSubCategoryMap(categoryMap);
                setAllSubCategories(Array.from(subCategoriesSet));
                return;
              }

              const matchKey = Object.keys(prev).find(
                (existingKey) =>
                  incomingKey
                    .toLowerCase()
                    .includes(existingKey.toLowerCase().replace(/\s/g, '')) // remove space from key before match
              );

              if (matchKey && Array.isArray(values)) {
                if (matchKey === 'Sub Category') {
                  updated[matchKey] = {
                    ALL: 'All',
                    'POTATO CHIPS': 'Potato Chips',
                    TORTILLA: 'Tortilla',
                    EXTRUDED: 'Extruded',
                   // 'SALTY SNACK': 'SALTY SNACKS',
                  };
                }
                // else if (matchKey === "Year" && data.year_month) {
                //   // Extract years from year_month data
                //   const years = Array.from(
                //     new Set(
                //       data.year_month.map((ym: string) =>
                //         parseInt(ym.split("_")[0])
                //       )
                //     )
                //   );
                //   updated[matchKey] = Object.fromEntries(
                //     years
                //       .sort((a, b) => b - a)
                //       .map((year) => [year.toString(), year])
                //   );
                // } else if (matchKey === "Month") {
                //   const monthNames = [
                //     "January",
                //     "February",
                //     "March",
                //     "April",
                //     "May",
                //     "June",
                //     "July",
                //     "August",
                //     "September",
                //     "October",
                //     "November",
                //     "December",
                //   ];

                // const selectedYear = filters.year;
                // const availableMonths =
                //   availableMonthsByYear[selectedYear] || [];

                // Create object with all months first
                // const monthEntries = monthNames
                //   .map((name, index) => [name, index + 1] as [string, number])
                //   .filter(([_, monthNum]) =>
                //     availableMonths.includes(monthNum)
                //   );

                // Always include ALL option
                // updated[matchKey] = {
                //   ALL: "ALL",
                //   ...Object.fromEntries(monthEntries),
                // };

                // console.log(
                //   "Available months for year",
                //   selectedYear,
                //   ":",
                //   availableMonths
                // );
                // console.log("Updated month options:", updated[matchKey]);
                // }
                else {
                  updated[matchKey] = {
                    ALL: 'ALL',
                    ...Object.fromEntries(values.map((val) => [val, val])),
                  };
                }
              }
            });
            setSyncFilters(false);
            return updated;
          });
        } catch (err) {
          console.error('Filter API failed, using Constants.tsx values:', err);

          setFilters((prevFilters) => ({
            ...prevFilters,
            // year: Object.values(filterOptions.Year)[0],
            // month: Object.values(filterOptions.Month)[0],
          }));

          // Create a year-to-month mapping from the Constants
          // const yearMonthMap: Record<number, number[]> = {};
          // // For each year in filterOptions
          // Object.values(filterOptions.Year).forEach((year) => {
          //   yearMonthMap[year] = Object.values(filterOptions.Month);
          // });
          // setAvailableMonthsByYear(yearMonthMap);

          // Use all options from Constants.tsx
          setUpdatedFilters((prev) => {
            if (!prev) return filterOptions;
            return {
              ...prev,
              // Year: filterOptions.Year,
              // Month: filterOptions.Month,
              Retailer: filterOptions.Retailer,
            };
          });
        }
      };
      fetchDataAsync();
    }
  }, [filters, syncFilters]);

  const getFilterStyles2 = (flag: boolean) => {
    if (!flag) return null;

    // const formattedRange = filters.start_month && filters.end_month
    // ? `${formatMonth(filters.start_month)} - ${formatMonth(filters.end_month)}`
    // : "Select Period";
    //   const baseRangeText = filters.base_start_month && filters.base_end_month
    //   ? `${formatMonth(filters.base_start_month)} - ${formatMonth(filters.base_end_month)}`
    //   : "Period 1";

    // const compRangeText = filters.comp_start_month && filters.comp_end_month
    //   ? `${formatMonth(filters.comp_start_month)} - ${formatMonth(filters.comp_end_month)}`
    //   : "Period 2";

    const handleBaseRangeSelection = (start: string, end: string) => {
      setFilters((prev) => ({
        ...prev,
        base_start_month: start,
        base_end_month: end,
      }));
      setFiltersApplied(false);
    };
    const handleCompRangeSelection = (start: string, end: string) => {
      setFilters((prev) => ({
        ...prev,
        comp_start_month: start,
        comp_end_month: end,
      }));
      setFiltersApplied(false);
    };

    const handleBaseRangeClear = () => {
      setFilters((prev) => ({
        ...prev,
        base_start_month: '',
        base_end_month: '',
      }));
    };

    const handleCompRangeClear = () => {
      setFilters((prev) => ({
        ...prev,
        comp_start_month: '',
        comp_end_month: '',
      }));
    };
    const isBaseActive =
      (!!filters.base_start_month && !!filters.base_end_month) ||
      activePicker === 'base';

    const isCompActive =
      (!!filters.comp_start_month && !!filters.comp_end_month) ||
      activePicker === 'comp';

    const sharedMenuProps = {
      PaperProps: {
        style: { maxHeight: 300, zIndex: 40000, overflow: 'visible' as const },
      },
      anchorOrigin: {
        vertical: 'bottom' as const,
        horizontal: 'left' as const,
      },
      transformOrigin: {
        vertical: 'top' as const,
        horizontal: 'left' as const,
      },
      container: typeof window !== 'undefined' ? document.body : undefined,
    };

    const fullMenuProps = {
      PaperProps: {
        style: {
          maxHeight: 300,
          zIndex: 40000,
          overflow: 'auto' as const,
          whiteSpace: 'normal' as const,
        },
      },
      anchorOrigin: {
        vertical: 'bottom' as const,
        horizontal: 'left' as const,
      },
      transformOrigin: {
        vertical: 'top' as const,
        horizontal: 'left' as const,
      },
      container: typeof window !== 'undefined' ? document.body : undefined,
    };

    return (
      <div
        className={styles.filterPanelContainer}
        style={{ marginTop: '0px' }}
      >
        <div
          className={styles.filterAccordionHeader}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* <h2 className={styles.filterTitle}>
            The Default Model will provide the insights for YTD vs YAGO for
            Salty Snacks Category for Complete UAE across all Retailers. To
            generate the deep dive insights in any specific area of focus,
            please select respective filters and click on Generate Insight
            button.{" "}
            {dataRange.start &&
              dataRange.end &&
              ` (Insights are based on available months only from ${formatMonth(
                dataRange.start
              )} to ${formatMonth(dataRange.end)})`}
          </h2> */}
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div className={styles.filterPanelContent}>
            <Stack
              direction='row'
              // This sets the gap between items
              spacing={{ xs: 2.5, sm: 4 }} // Corresponds to 12px and 16px
              rowGap={0.5} // Sets the gap between rows
              useFlexGap
              sx={{
                flexWrap: 'wrap',
                marginBottom: '5px',
                width: '100%',
                alignItems: 'flex-end',
              }}
            >
              {/* Year Dropdown
                <div className={styles.filterItem}>
                  <label className={styles.filterItemLabel}>Year</label>
                  <FormControl
                    size="medium"
                    sx={{
                      minWidth: "80px",
                      maxWidth: "200px",
                      "& .MuiInputBase-root": {
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        height: "25px",
                        fontSize: "0.65rem",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        paddingTop: "-10px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#CBD5E0",
                      },
                    }}
                  > */}
              {/* <Select
                      labelId="year"
                      id="year"
                      value={String(filters.year)}
                      disabled={syncFilters || disableSummaryBtn}
                      name="year"
                      onChange={handleChangeEvent}
                    >
                      {updatedFilters?.Year &&
                        Object.entries(updatedFilters.Year).map(
                          ([yearKey, yearValue]) => (
                            <MenuItem
                              key={yearKey}
                              sx={{ fontSize: "0.75rem" }}
                              value={yearValue}
                            >
                              {yearValue}
                            </MenuItem>
                          )
                        )}
                    </Select>
                  </FormControl>
                </div> */}

              {/* Month Dropdown
                <div className={styles.filterItem}>
                  <label className={styles.filterItemLabel}>Month</label>
                  <FormControl
                    size="medium"
                    sx={{
                      minWidth: "80px",
                      maxWidth: "200px",
                      "& .MuiInputBase-root": {
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        height: "25px",
                        fontSize: "0.65rem",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        paddingTop: "-10px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#CBD5E0",
                      },
                    }}
                  >
                    <Select
                      labelId="month"
                      id="month"
                      value={String(filters.month)}
                      disabled={syncFilters || disableSummaryBtn}
                      name="month"
                      onChange={handleChangeEvent}
                    >
                      {availableMonthsByYear[filters.year]?.map((month) => {
                        const monthNames = [
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ];
                        return (
                          <MenuItem
                            key={month}
                            sx={{ fontSize: "0.75rem" }}
                            value={month}
                          >
                            {monthNames[month - 1]}
                          </MenuItem>
                        );
                      }) ||
                        (updatedFilters?.Month &&
                          Object.entries(updatedFilters.Month).map(
                            ([monthName, monthValue]) => (
                              <MenuItem
                                key={monthName}
                                sx={{ fontSize: "0.75rem" }}
                                value={monthValue}
                              >
                                {monthName}
                              </MenuItem>
                            )
                          ))}
                    </Select>
                  </FormControl>
                </div> */}

              {updatedFilters &&
                Object.entries(updatedFilters)
                  .filter(
                    ([category]) =>
                      !['Year', 'Month', 'use_cache'].includes(category)
                  )
                  .sort(([a], [b]) => {
                    if (a === 'Sub Category') return -1;
                    if (b === 'Sub Category') return 1;
                    return a.localeCompare(b);
                  })
                  .map(([category, options]) => {
                    const getFilterKey = (
                      category: string
                    ): keyof FiltersState => {
                      switch (category) {
                        case 'Category':
                          return 'category';
                        case 'Sub Category':
                          return 'subCategory';
                        case 'Region':
                          return 'region';
                        case 'Retailer':
                          return 'retailer';
                        case 'use_cache':
                          return 'use_cache';
                        default:
                          return 'subCategory';
                      }
                    };

                    const filterKey = getFilterKey(category);
                    const optionEntries = Object.entries(options).filter(
                      ([key]) => key.toUpperCase() !== 'ALL'
                    );

                    if (category === 'Category') {
                      return null;
                    }

                    if (category === 'Sub Category') {
                      optionEntries.sort((a, b) =>
                        String(a[0])
                          .toLowerCase()
                          .localeCompare(String(b[0]).toLowerCase())
                      );

                      const saltedIndex = optionEntries.findIndex(
                        ([k]) => k.toUpperCase() === 'SALTY SNACKS'
                      );
                      // if (saltedIndex === -1) {
                      //   // add SALTY SNACKS at the start if not present
                      //   optionEntries.unshift(['SALTY SNACKS', 'SALTY SNACKS']);
                      // } else
                        if (saltedIndex > 0) {
                        // move existing SALTY SNACKS to the front
                        const [salty] = optionEntries.splice(saltedIndex, 1);
                        optionEntries.unshift(salty);
                      }

                      return (
                        <div
                          key={category}
                          className={styles.categoryWithPeriodRow}
                          style={{
                            display: 'flex',
                            gap: 12,
                            width: '100%',
                            alignItems: 'stretch',
                            flexWrap: 'nowrap',
                          }}
                        >
                          {/* Left column: Category tile + separate "Use pre-generated insights" tile stacked */}
                          <div
                            style={{
                              maxWidth: '320px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 10.5,
                            }}
                          >
                            {/* Category and sub-category dropdowns */}
                            <div
                              className={styles.filterTileCard}
                              style={{
                                flex: 4,
                                overflow: 'hidden',
                                minHeight: 0,
                              }}
                            >
                              <div style={{ display: 'grid', gap: 20 }}>
                                <FilterDropdown
                                  label='Category'
                                  entries={
                                    updatedFilters && updatedFilters['Category']
                                      ? Object.entries(
                                          updatedFilters['Category']
                                        ).filter(
                                          ([k]) => k.toUpperCase() !== 'ALL'
                                        )
                                      : []
                                  }
                                  value={filters.category || []}
                                  forceMultipleLabel={
                                    !!scopedAllFlags['category']
                                  }
                                  isExplicitAll={!!scopedAllFlags['category']}
                                  onSelectAll={() =>
                                    handleSelectAll(
                                      'category',
                                      updatedFilters &&
                                        (updatedFilters as any)['Category']
                                        ? (updatedFilters as any)['Category']
                                        : (filterOptions as any)['Category']
                                    )
                                  }
                                  onToggle={(val) =>
                                    handleToggleOption('category', val)
                                  }
                                  onClear={() => handleClearSelection('category')}
                                  menuProps={fullMenuProps}
                                  disabled={syncFilters || disableSummaryBtn}
                                  placeholder='All'
                                  searchable={true}
                                />

                                <FilterDropdown
                                  label='Sub Category'
                                  entries={(() => {
                                    const selectedCategories =
                                      filters.category || [];
                                    if (
                                      !categoryToSubCategoryMap ||
                                      selectedCategories.length === 0
                                    ) {
                                      return optionEntries;
                                    }

                                    const allowed = new Set<string>();
                                    selectedCategories.forEach((category) => {
                                      const subSet =
                                        categoryToSubCategoryMap[category];
                                      if (subSet) {
                                        subSet.forEach((sub) =>
                                          allowed.add(sub)
                                        );
                                      }
                                    });

                                    return optionEntries.filter(([, value]) =>
                                      allowed.has(String(value))
                                    );
                                  })()}
                                  value={filters.subCategory || []}
                                  forceMultipleLabel={
                                    !!scopedAllFlags['subCategory']
                                  }
                                  isExplicitAll={
                                    !!scopedAllFlags['subCategory']
                                  }
                                  onSelectAll={() =>
                                    handleSelectAll(
                                      'subCategory',
                                      {
                                        ALL: 'ALL',
                                        ...Object.fromEntries(
                                          ((() => {
                                            const selectedCategories =
                                              filters.category || [];
                                            if (
                                              !categoryToSubCategoryMap ||
                                              selectedCategories.length === 0
                                            ) {
                                              return optionEntries;
                                            }

                                            const allowed = new Set<string>();
                                            selectedCategories.forEach(
                                              (category) => {
                                                const subSet =
                                                  categoryToSubCategoryMap[
                                                    category
                                                  ];
                                                if (subSet) {
                                                  subSet.forEach((sub) =>
                                                    allowed.add(sub)
                                                  );
                                                }
                                              }
                                            );

                                            return optionEntries.filter(
                                              ([, value]) =>
                                                allowed.has(String(value))
                                            );
                                          })() as Array<[string, string]>)
                                            .map(([k, v]) => [k, v])
                                        ),
                                      }
                                    )
                                  }
                                  onToggle={(val) =>
                                    handleToggleOption('subCategory', val)
                                  }
                                  onClear={() =>
                                    handleClearSelection('subCategory')
                                  }
                                  menuProps={fullMenuProps}
                                  disabled={syncFilters || disableSummaryBtn}
                                  placeholder='All'
                                  searchable={true}
                                />
                              </div>
                            </div>

                            {/* Separate tile for Use pre-generated insights - stacked under Category */}
                            <div
                              className={
                                styles.filterTileCard +
                                ' ' +
                                styles.smallFilterTile
                              }
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flex: '0 0 auto',
                                minHeight: 54,
                                gap: 7,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <Tooltip
                                  title='Use cached insights for faster results'
                                  placement='right'
                                >
                                  <span
                                    className={styles.filterGroupName}
                                    style={{
                                      fontSize: 12,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Use pre-generated insights
                                  </span>
                                </Tooltip>
                              </div>
                              <div>
                                <Switch
                                  checked={filters.use_cache === 'true'}
                                  onChange={(e) =>
                                    handleFilterChange(
                                      'use_cache',
                                      String(e.target.checked)
                                    )
                                  }
                                  name='use_cache'
                                  disabled={syncFilters || disableSummaryBtn}
                                  size='small'
                                />
                              </div>
                            </div>
                          </div>

                          {/* Period Comparison panel on the right */}
                          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                              }}
                            >
                              {/* single compact tile containing both pickers + apply button */}
                              <div
                                className={
                                  styles.filterTileCard +
                                  ' ' +
                                  styles.smallFilterTile
                                }
                                style={{ marginTop: 0, width: '100%' }}
                              >
                                <h4
                                  style={{
                                    margin: 0,
                                    fontSize: '.7rem',
                                    fontWeight: 'bold',
                                    paddingBottom: 10,
                                  }}
                                >
                                  <span>Period Comparison </span>
                                  <span
                                    style={{
                                      color: '#327ec9',
                                      fontStyle: 'italic',
                                      fontWeight: 400,
                                    }}
                                  >
                                    (Select appropriate periods for comparison
                                    and click on Apply Selection to continue)
                                  </span>
                                </h4>
                                <div className={styles.responsiveContainer}>
                                  <div
                                    className={styles.pickerColumn}
                                    style={{ flex: 0.6, paddingRight: 7 }}
                                  >
                                    <MonthRangePicker
                                      availableMonths={availableMonths}
                                      initialStartMonth={
                                        filters.base_start_month
                                      }
                                      initialEndMonth={filters.base_end_month}
                                      onRangeSelect={handleBaseRangeSelection}
                                      onRangeClear={handleBaseRangeClear}
                                      disabledRange={{
                                        start: filters.comp_start_month,
                                        end: filters.comp_end_month,
                                      }}
                                      disableAfter={
                                        filters.comp_end_month || undefined
                                      }
                                    />
                                    <div
                                      className={styles.selectedRangeBox}
                                      style={{ marginTop: 5 }}
                                    >
                                      {filters.base_start_month &&
                                        filters.base_end_month ? (
                                        <>
                                          {getSelectedMonthsDisplay(
                                            filters.base_start_month,
                                            filters.base_end_month
                                          )}
                                        </>
                                      ) : (
                                        <>Period 1 (From – To)</>
                                      )}
                                    </div>
                                  </div>

                                  <div
                                    className={styles.pickerColumn}
                                    style={{ flex: 0.6, paddingLeft: 7 }}
                                  >
                                    <MonthRangePicker
                                      availableMonths={availableMonths}
                                      initialStartMonth={
                                        filters.comp_start_month
                                      }
                                      initialEndMonth={filters.comp_end_month}
                                      onRangeSelect={handleCompRangeSelection}
                                      onRangeClear={handleCompRangeClear}
                                      disabledRange={{
                                        start: filters.base_start_month,
                                        end: filters.base_end_month,
                                      }}
                                      disableBefore={
                                        filters.base_start_month || undefined
                                      }
                                    />
                                    <div
                                      className={styles.selectedRangeBox}
                                      style={{ marginTop: 5 }}
                                    >
                                      {filters.comp_start_month &&
                                        filters.comp_end_month ? (
                                        <>
                                          {getSelectedMonthsDisplay(
                                            filters.comp_start_month,
                                            filters.comp_end_month
                                          )}
                                        </>
                                      ) : (
                                        <>Period 2 (From – To)</>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: 12,
                                  }}
                                >
                                  <div className={styles.filter_button}>
                                    {/* Data Availability button: opens PowerBI dashboard in new tab */}
                                    <Tooltip
                                      title='Click to view EPOS data availability on the Power BI dashboard.'
                                      placement='top'
                                      componentsProps={{
                                        tooltip: { sx: { maxWidth: 360 } },
                                      }}
                                    >
                                      <span style={{ flex: 1, display: 'flex' }}>
                                        <Button
                                          variant='contained'
                                          size='small'
                                          onClick={() => {
                                            try {
                                              const url =
                                                currentDataAvailabilityUrls.epos;
                                              // Telemetry: user clicked Data Availability
                                              try {
                                                callLogEvent(
                                                  'CLICKED_DATA_AVAILABILITY',
                                                  'User opened Data Availability(EPOS) PowerBI dashboard'
                                                );
                                              } catch (e) {
                                                /* ignore telemetry errors */
                                              }
                                              window.open(url, '_blank');
                                            } catch (e) {
                                              console.error(
                                                'Failed to open Data Availability URL',
                                                e
                                              );
                                            }
                                          }}
                                          sx={{
                                            backgroundImage: 'rgb(14, 90, 167)',
                                            backgroundColor: 'rgb(14, 90, 167)',
                                            borderRadius: '8px',
                                            height: '32px',
                                            minWidth: 0,
                                            width: '100%',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: 'white',
                                            textTransform: 'none',
                                            padding: '6px 12px',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                              backgroundImage: 'rgb(14, 90, 167)',
                                            },
                                          }}
                                        >
                                          Data Availability(EPOS)
                                        </Button>
                                      </span>
                                    </Tooltip>

                                    {/* Additional Data Availability button (alternate report) */}
                                    <Tooltip
                                      title='Click to view Merchandizing data availability on the Power BI dashboard.'
                                      placement='top'
                                      componentsProps={{
                                        tooltip: { sx: { maxWidth: 380 } },
                                      }}
                                    >
                                      <span style={{ flex: 1, display: 'flex' }}>
                                        <Button
                                          variant='contained'
                                          size='small'
                                          onClick={() => {
                                            try {
                                              const url =
                                                currentDataAvailabilityUrls.merchandizing;
                                              try {
                                                callLogEvent(
                                                  'CLICKED_DATA_AVAILABILITY_ALTERNATE',
                                                  'User opened Data Availability Merchandizing PowerBI report'
                                                );
                                              } catch (e) {
                                                /* ignore telemetry errors */
                                              }
                                              window.open(url, '_blank');
                                            } catch (e) {
                                              console.error(
                                                'Failed to open alternate Data Availability URL',
                                                e
                                              );
                                            }
                                          }}
                                          sx={{
                                            backgroundImage: 'rgb(14, 90, 167)',
                                            backgroundColor: 'rgb(14, 90, 167)',
                                            borderRadius: '8px',
                                            height: '32px',
                                            minWidth: 0,
                                            width: '100%',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: 'white',
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          Data Availability(Merchandizing)
                                        </Button>
                                      </span>
                                    </Tooltip>

                                    <Tooltip
                                      title='Click here to enable region and retailers filters'
                                      placement='top'
                                    >
                                      <span style={{ flex: 1, display: 'flex' }}>
                                        <Button
                                          variant='contained'
                                          size='small'
                                          onClick={handleApplyFilters}
                                          disabled={
                                            isApplyingFilters ||
                                            syncFilters ||
                                            disableSummaryBtn ||
                                            !filters.base_start_month ||
                                            !filters.base_end_month ||
                                            !filters.comp_start_month ||
                                            !filters.comp_end_month ||
                                            false
                                          }
                                          sx={{
                                            backgroundImage: 'rgb(14, 90, 167)',
                                            backgroundColor: 'rgb(14, 90, 167)',
                                            borderRadius: '8px',
                                            height: '32px',
                                            minWidth: 0,
                                            width: '100%',
                                            fontSize: '0.85rem',
                                            fontWeight: '700',
                                            color: 'white',
                                            textTransform: 'none',
                                            padding: '6px 14px',
                                            '&:hover': {
                                              backgroundImage: 'rgb(14, 90, 167)',
                                            },
                                          }}
                                        >
                                          {isApplyingFilters ? (
                                            <CircularProgress
                                              size={14}
                                              disableShrink
                                              sx={{ color: 'white' }}
                                            />
                                          ) : (
                                            'Apply Selection'
                                          )}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // Render Region + Retailer side-by-side; keep Salty Snacks separate
                    if (
                      [
                        'Region',
                        'Sub Region',
                        'Retailer',
                        'City',
                        'Promo Flag',
                        'Salty Snacks',
                      ].includes(category)
                    ) {
                      // These are rendered inside the combined geo/product tile under Region.
                      if (
                        ['Sub Region', 'Retailer', 'City', 'Promo Flag'].includes(
                          category
                        )
                      )
                        return null;

                      // For Region (and optionally Salty Snacks), render either combined Region+Retailer or fallback
                      if (category === 'Region') {
                        const regionEntries = optionEntries; // from options for Region
                        // Compute retailer entries scoped to selected regions when combinations mapping exists
                        let retailerEntries =
                          updatedFilters && updatedFilters['Retailer']
                            ? Object.entries(updatedFilters['Retailer']).filter(
                              ([k]) => k.toUpperCase() !== 'ALL'
                            )
                            : [];

                        const selectedRegions =
                          (filters.region as string[]) || [];
                        if (
                          regionRetailerMap &&
                          selectedRegions &&
                          selectedRegions.length > 0
                        ) {
                          const allowed = new Set<string>();
                          selectedRegions.forEach((r) => {
                            const s = regionRetailerMap[r];
                            if (s) s.forEach((ret) => allowed.add(ret));
                          });
                          retailerEntries = retailerEntries.filter(([k, v]) =>
                            allowed.has(v as string)
                          );
                          // Do NOT call setFilters here (render path) — synchronization happens in useEffect below
                        }

                        // Combine Region and Retailer into a single tile with two explicit rows
                        return (
                          <React.Fragment key='region-retailer-section'>
                            <div style={{ width: '100%', margin: '5px 0' }} />
                            <div
                              key='region-retailer-row'
                              className={styles.regionRetailerRow}
                              style={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'flex-start',
                                flexWrap: isMobile ? 'wrap' : 'nowrap',
                                gap: 8,
                              }}
                            >
                              <div
                                className={styles.filterTileCard}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  width: '100%',
                                  gap: 8,
                                  padding: 8,
                                }}
                              >
                                <div
                                  className={styles.generateInsightsGridRow}
                                >
                                  <div
                                    className={styles.generateInsightsGridCell}
                                  >
                                    <FilterDropdown
                                      label='Region'
                                      entries={
                                        regionEntries as Array<[string, string]>
                                      }
                                      value={filters.region}
                                      forceMultipleLabel={
                                        !!scopedAllFlags['region']
                                      }
                                      isExplicitAll={!!scopedAllFlags['region']}
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'region',
                                          updatedFilters &&
                                            (updatedFilters as any)['Region']
                                            ? (updatedFilters as any)['Region']
                                            : (filterOptions as any)['Region']
                                        )
                                      }
                                      onToggle={(val) =>
                                        handleToggleOption('region', val)
                                      }
                                      onClear={() =>
                                        handleClearSelection('region')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='Select Region'
                                      searchable={true}
                                    />
                                  </div>

                                  <div
                                    className={styles.generateInsightsGridCell}
                                  >
                                    <FilterDropdown
                                      label='Sub Region'
                                      entries={
                                        (updatedFilters &&
                                          updatedFilters['Sub Region']
                                          ? Object.entries(
                                            updatedFilters['Sub Region']
                                          ).filter(
                                            ([k]) => k.toUpperCase() !== 'ALL'
                                          )
                                          : []) as Array<[string, string]>
                                      }
                                      value={(filters as any)['sub_region'] || []}
                                      forceMultipleLabel={
                                        !!scopedAllFlags['sub_region']
                                      }
                                      isExplicitAll={
                                        !!scopedAllFlags['sub_region']
                                      }
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'sub_region',
                                          updatedFilters &&
                                            (updatedFilters as any)['Sub Region']
                                            ? (updatedFilters as any)['Sub Region']
                                            : ((filterOptions as any)['Sub Region'] || {})
                                        )
                                      }
                                      onToggle={(val) =>
                                        handleToggleOption('sub_region', val)
                                      }
                                      onClear={() =>
                                        handleClearSelection('sub_region')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='Select Sub Region'
                                      searchable={true}
                                    />
                                  </div>

                                  {/* City */}
                                  <div
                                    className={styles.generateInsightsGridCell}
                                  >
                                    <FilterDropdown
                                      label='City'
                                      entries={
                                        (updatedFilters &&
                                          updatedFilters['City']
                                          ? Object.entries(
                                            updatedFilters['City']
                                          ).filter(
                                            ([k]) => k.toUpperCase() !== 'ALL'
                                          )
                                          : []) as Array<[string, string]>
                                      }
                                      value={(filters as any)['city'] || []}
                                      forceMultipleLabel={
                                        !!scopedAllFlags['city']
                                      }
                                      isExplicitAll={!!scopedAllFlags['city']}
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'city',
                                          updatedFilters &&
                                            (updatedFilters as any)['City']
                                            ? (updatedFilters as any)['City']
                                            : ((filterOptions as any)['City'] || {})
                                        )
                                      }
                                      onToggle={(val: string) =>
                                        handleToggleOption('city', val)
                                      }
                                      onClear={() =>
                                        handleClearSelection('city')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='City'
                                      searchable={true}
                                    />
                                  </div>

                                  <div
                                    className={styles.generateInsightsGridCell}
                                  >
                                    <FilterDropdown
                                      label='Retailer'
                                      entries={
                                        retailerEntries as Array<
                                          [string, string]
                                        >
                                      }
                                      value={filters.retailer}
                                      forceMultipleLabel={
                                        !!scopedAllFlags['retailer']
                                      }
                                      isExplicitAll={
                                        !!scopedAllFlags['retailer']
                                      }
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'retailer',
                                          updatedFilters &&
                                            (updatedFilters as any)['Retailer']
                                            ? (updatedFilters as any)[
                                            'Retailer'
                                            ]
                                            : (filterOptions as any)['Retailer']
                                        )
                                      }
                                      onToggle={(val) =>
                                        handleToggleOption('retailer', val)
                                      }
                                      onClear={() =>
                                        handleClearSelection('retailer')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='All'
                                      searchable={true}
                                    />
                                  </div>

                                  {/* Channel */}
                                  <div
                                    className={styles.generateInsightsGridCell}
                                  >
                                    <FilterDropdown
                                      label='Channel'
                                      entries={
                                        (updatedFilters &&
                                          updatedFilters['Channel']
                                            ? Object.entries(
                                                updatedFilters['Channel']
                                              ).filter(
                                                ([k]) => k.toUpperCase() !== 'ALL'
                                              )
                                            : []) as Array<[string, string]>
                                      }
                                      value={(filters as any)['channel'] || []}
                                      forceMultipleLabel={
                                        !!scopedAllFlags['channel']
                                      }
                                      isExplicitAll={
                                        !!scopedAllFlags['channel']
                                      }
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'channel',
                                          updatedFilters &&
                                            (updatedFilters as any)['Channel']
                                            ? (updatedFilters as any)['Channel']
                                            : (filterOptions as any)['Channel']
                                        )
                                      }
                                      onToggle={(val: string) =>
                                        handleToggleOption('channel', val)
                                      }
                                      onClear={() =>
                                        handleClearSelection('channel')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='Channel'
                                      searchable={true}
                                    />
                                  </div>

                                  <div
                                    className={`${styles.generateInsightsGridCell} ${styles.generateInsightsGridRetailerStore}`}
                                  >
                                    <FilterDropdown
                                      label='Retailer Store'
                                      entries={
                                        (updatedFilters &&
                                          updatedFilters['Retailer Store']
                                          ? Object.entries(
                                            updatedFilters['Retailer Store']
                                          ).filter(
                                            ([k]) => k.toUpperCase() !== 'ALL'
                                          )
                                          : []) as Array<[string, string]>
                                      }
                                      value={
                                        (filters as any)['retailer_store'] || []
                                      }
                                      forceMultipleLabel={
                                        !!scopedAllFlags['retailer_store']
                                      }
                                      isExplicitAll={
                                        !!scopedAllFlags['retailer_store']
                                      }
                                      onSelectAll={() =>
                                        handleSelectAll(
                                          'retailer_store',
                                          updatedFilters &&
                                            (updatedFilters as any)[
                                            'Retailer Store'
                                            ]
                                            ? (updatedFilters as any)[
                                            'Retailer Store'
                                            ]
                                            : (filterOptions as any)[
                                            'Retailer Store'
                                            ]
                                        )
                                      }
                                      onToggle={(val: string) =>
                                        handleToggleOption(
                                          'retailer_store',
                                          val
                                        )
                                      }
                                      onClear={() =>
                                        handleClearSelection('retailer_store')
                                      }
                                      menuProps={fullMenuProps}
                                      disabled={
                                        !filtersApplied ||
                                        syncFilters ||
                                        disableSummaryBtn
                                      }
                                      placeholder='Retailer Store'
                                      searchable={true}
                                    />
                                  </div>
                                </div>

                                <div
                                  className={styles.generateInsightsGridRow}
                                >
                                  {[
                                    {
                                      key: 'Manufacturer',
                                      fk: 'manufacturer' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Brand',
                                      fk: 'brand' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Sub Brand',
                                      fk: 'sub_brand' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Pack Group',
                                      fk: 'pack_group' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Pack Size',
                                      fk: 'pack_size_group' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Price Point',
                                      fk: 'price_point' as keyof FiltersState,
                                    },
                                    {
                                      key: 'Promo Flag',
                                      fk: 'promo_flag' as keyof FiltersState,
                                    },
                                  ].map((info) => (
                                    <div
                                      key={info.key}
                                      className={styles.generateInsightsGridCell}
                                    >
                                      <FilterDropdown
                                        label={info.key}
                                        entries={
                                          updatedFilters &&
                                            updatedFilters[info.key]
                                            ? Object.entries(
                                              updatedFilters[info.key]
                                            ).filter(
                                              ([k]) =>
                                                k.toUpperCase() !== 'ALL'
                                            )
                                            : []
                                        }
                                        value={(filters as any)[info.fk] || []}
                                        forceMultipleLabel={
                                          !!scopedAllFlags[info.fk as string]
                                        }
                                        isExplicitAll={
                                          !!scopedAllFlags[info.fk as string]
                                        }
                                        onSelectAll={() =>
                                          handleSelectAll(
                                            info.fk as any,
                                            updatedFilters &&
                                              (updatedFilters as any)[info.key]
                                              ? (updatedFilters as any)[
                                              info.key
                                              ]
                                              : (filterOptions as any)[info.key]
                                          )
                                        }
                                        onToggle={(val) =>
                                          handleToggleOption(
                                            info.fk as any,
                                            val
                                          )
                                        }
                                        onClear={() =>
                                          handleClearSelection(info.fk)
                                        }
                                        menuProps={fullMenuProps}
                                        disabled={
                                          !filtersApplied ||
                                          syncFilters ||
                                          disableSummaryBtn
                                        }
                                        placeholder={info.key}
                                        searchable={true}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      }

                      // Fall back: render Salty Snacks or other single groups normally
                      const currentSelection =
                        (filters[filterKey] as string[]) || [];
                      return (
                        <div
                          key={category}
                          className={styles.filterGroup}
                        >
                          <div className={styles.filterGroupHeader}>
                            <span className={styles.filterGroupName}>
                              {category === 'Sub Category'
                                ? 'Category'
                                : category}
                            </span>
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                              }}
                            >
                              <button
                                className={styles.selectAllButton}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() =>
                                  handleSelectAll(filterKey, options)
                                }
                                disabled={syncFilters || disableSummaryBtn}
                                title='Select All'
                              >
                                Select All
                              </button>
                              <button
                                className={styles.selectAllButton}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleClearSelection(filterKey)}
                                disabled={syncFilters || disableSummaryBtn}
                                title='Clear'
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                          <div className={styles.chipContainer}>
                            {optionEntries.map(([key, value]) => (
                              <React.Fragment key={key}>
                                <input
                                  type='checkbox'
                                  id={`${filterKey}-${key}`}
                                  name={filterKey}
                                  value={value as string}
                                  checked={currentSelection.includes(
                                    value as string
                                  )}
                                  onChange={() =>
                                    handleMultiSelectChange(
                                      filterKey,
                                      value as string
                                    )
                                  }
                                  className={styles.chipRadio}
                                  disabled={syncFilters || disableSummaryBtn}
                                />
                                <label
                                  htmlFor={`${filterKey}-${key}`}
                                  className={styles.chipLabel}
                                >
                                  {key}
                                </label>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    // 'use_cache' is rendered inline above; skip here
                    if (filterKey === 'use_cache') return null;

                    return null;
                  })}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <FormControl
                  size='small'
                  sx={{
                    minWidth: 60,
                    '& .MuiButtonBase-root': {
                      backgroundImage:
                        'linear-gradient(to right, #A084E8, #86A8E7)',
                      borderRadius: '8px',
                      height: '30px', // ⬅️ Same as Select height
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      color: 'white',
                      textTransform: 'none',
                      padding: '0px 20px',
                      '&:hover': {
                        backgroundImage:
                          'linear-gradient(to right, #9073D8, #7697D7)',
                      },
                    },
                  }}
                >
                  {/* <Button

                    onClick={() => {
                      handleFilterSummary();
                      setSyncFilters(true);
                      // setIsSummaryGenerating(true);
                    }}
                    variant="contained"
                    size="small"
                    // sx={{
                    // 	fontSize: '0.75rem',
                    // 	fontWeight: 'bold',
                    // 	height: '32px',
                    // 	minWidth: 160,
                    // 	color: 'white',
                    // 	backgroundImage: 'linear-gradient(to right, #A084E8, #86A8E7)',
                    // 	textTransform: 'none',
                    // 	borderRadius: '20px',
                    // 	padding: '0px 20px',
                    // 	'&:hover': {
                    // 		backgroundImage: 'linear-gradient(to right, #9073D8, #7697D7)',
                    // 	},
                    // }}
                    disabled={
                      syncFilters ||
                      isSummaryGenerating ||
                      disableSummaryBtn ||
                      !filters.base_start_month ||
                      !filters.base_end_month ||
                      !filters.comp_start_month ||
                      !filters.comp_end_month
                    }
                  >
                    {syncFilters || isSummaryGenerating || disableSummaryBtn ? (
                      <CircularProgress
                        size={22}
                        disableShrink
                        sx={{ color: "white" }}
                      />
                    ) : (
                      "Generate AI Insight"
                    )}
                  </Button> */}
                </FormControl>
              </div>
            </Stack>
          </div>
        </div>
      </div>
    );
  };

  const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);
  useEffect(() => {
    onSummaryToolbarStateChange?.({
      syncFilters,
      isSummaryGenerating,
    });
  }, [onSummaryToolbarStateChange, syncFilters, isSummaryGenerating]);

  // const handleFilterSummary = () => {
  // 	console.log("1. Clicked! Setting isSummaryGenerating to TRUE.");
  //     setIsSummaryGenerating(true);
  // 	let payload = {
  // 		projName: title,
  // 		time_dimension: 'L1W',
  // 		filters,
  // 	};
  // 	// handleSummary2 && handleSummary2(payload);
  // 	// setIsSummaryGenerating(true);
  // 	// setTimeout(() => {
  // 	// 	setIsSummaryGenerating(false);
  // 	// }, 6000);
  // 	try {
  //         console.log("2. Calling parent's handleSummary2 and WAITING...");
  //         if (handleSummary2) { handleSummary2(payload);
  //             console.log("4. The 'await' has finished. Parent work is complete.");
  //         } else {
  //             console.error("handleSummary2 prop is missing!");
  //         }
  //     } catch (error) {
  //         console.error("Error caught in MessageList:", error);
  //     } finally {
  //         console.log("5. 'finally' block is running. Setting isSummaryGenerating to FALSE.");
  //         setIsSummaryGenerating(false);
  //     }
  // };
  // src/MessageList.js

  const handleFilterSummary = async () => {
    // callLogEvent(
    //   "CLICKED_GENERATE_AI_INSIGHT",
    //   `Filters: ${JSON.stringify(filters)}`
    // );
    setIsSummaryGenerating(true);
    const makeNormalizedKey = (k: string) =>
      k.replace(/[^a-z0-9]/gi, '').toLowerCase();

    const findUpdatedOptionsFor = (filterKey: string) => {
      if (!updatedFilters) return null;
      const norm = makeNormalizedKey(filterKey);
      const entry = Object.keys(updatedFilters).find(
        (cat) => makeNormalizedKey(cat) === norm
      );
      return entry ? updatedFilters[entry] : null;
    };

    const serializeSelection = (filterKey: string, selected: any[]) => {
      const arr = Array.isArray(selected) ? selected.filter(Boolean) : [];

      // Sub Category: empty (placeholder "All") → send empty; ALL selected → send "ALL"; specific → semicolon-separated
      if (filterKey === 'subCategory') {
        if (!arr.length) return '';
        if (arr.includes('ALL') || (scopedAllFlags && scopedAllFlags['subCategory'])) return 'ALL';
        return arr.slice().sort().join(';');
      }

      // Category: empty (default "All") → send all available values; ALL or all selected → send all values semicolon-separated; specific → semicolon-separated
      if (filterKey === 'category') {
        const opts =
          findUpdatedOptionsFor(filterKey) || (filterOptions as any)['Category'];
        const availableValues =
          opts && typeof opts === 'object'
            ? (Object.values(opts).filter(
                (v) =>
                  typeof v === 'string' && (v as string).toUpperCase() !== 'ALL'
              ) as string[])
            : [];

        // Empty (default "All" placeholder) → send all available values
        if (!arr.length) return availableValues.slice().sort().join(';');

        const explicitSelected = arr
          .map((v) => String(v || '').trim())
          .filter((v) => v && v.toUpperCase() !== 'ALL');
        const isAllSelected =
          arr.includes('ALL') ||
          (scopedAllFlags && scopedAllFlags['category']) ||
          (!!availableValues.length &&
            explicitSelected.length === availableValues.length);

        if (isAllSelected) {
          return availableValues.slice().sort().join(';');
        }

        return explicitSelected.slice().sort().join(';');
      }

      // For region/retailer: if nothing is selected OR every available option is selected,
      // send the full list of available options; otherwise send only the explicitly selected ones.
      if (filterKey === 'region' || filterKey === 'retailer') {
        const opts = findUpdatedOptionsFor(filterKey);
        const available =
          opts && typeof opts === 'object'
            ? Object.keys(opts).filter((k) => k.toUpperCase() !== 'ALL')
            : [];

        // Nothing selected -> send all available (expanded)
        if (!arr.length) {
          return available.length ? available.slice().sort().join(';') : '';
        }

        // All available selected -> send all available (expanded) instead of an "ALL" marker
        if (available.length && arr.length === available.length) {
          return available.slice().sort().join(';');
        }

        // Partial selection -> send only selected items
        return arr.slice().sort().join(';');
      }

      // Product group filters (manufacturer, brand, sub_brand, pack_group, pack_size_group, price_point)
      // should only send options that are both selected and currently valid per updatedFilters.
      const productFilterKeys = new Set([
        'manufacturer',
        'brand',
        'sub_brand',
        'pack_group',
        'pack_size_group',
        'price_point',
        // Also apply the same intersection rule for channel and retailer_store
        'sub_region',
        'city',
        'channel',
        'retailer_store',
      ]);

      if (productFilterKeys.has(filterKey)) {
        // If the user explicitly clicked Select All for this filter, always
        // send the literal "ALL" regardless of whether updatedFilters later
        // shrank the available option list.
        if (scopedAllFlags && scopedAllFlags[filterKey]) {
          return 'ALL';
        }
        const opts = findUpdatedOptionsFor(filterKey);
        let availableValues: string[] = [];
        if (opts && typeof opts === 'object') {
          // updatedFilters stores option objects mapping label -> value
          availableValues = Object.values(opts).filter(
            (v) =>
              typeof v === 'string' && (v as string).toUpperCase() !== 'ALL'
          ) as string[];
        }
        // If we have an explicit available list, intersect selected with it.
        if (availableValues && availableValues.length) {
          const validSelected = arr.filter((v) => availableValues.includes(v));
          return validSelected.slice().sort().join(';');
        }
        // Fallback: if updatedFilters isn't ready, just send the selected values
        return arr.slice().sort().join(';');
      }

      // For other filters, if the user selected every available option, send the literal "ALL"
      const opts = findUpdatedOptionsFor(filterKey);
      let available: string[] = [];
      if (opts && typeof opts === 'object') {
        available = Object.keys(opts).filter((k) => k.toUpperCase() !== 'ALL');
      }
      const isAllMarker = arr.includes('ALL');
      const explicitSelected = arr.filter((v) => String(v) !== 'ALL');

      if (isAllMarker) {
        if (available.length && explicitSelected.length === available.length)
          return 'ALL';
        // otherwise expand to the current available options to avoid sending stale ALL
        if (available.length) return available.slice().sort().join(';');
        return explicitSelected.slice().sort().join(';');
      }

      if (available.length && arr.length === available.length) return 'ALL';

      return arr.slice().sort().join(';');
    };

    const payloadFilters: any = {};
    // copy date fields and use_cache directly
    payloadFilters.base_start_month = filters.base_start_month || '';
    payloadFilters.base_end_month = filters.base_end_month
      ? incrementMonth(filters.base_end_month)
      : '';
    payloadFilters.comp_start_month = filters.comp_start_month || '';
    payloadFilters.comp_end_month = filters.comp_end_month
      ? incrementMonth(filters.comp_end_month)
      : '';

    // Serialize each selectable filter into a string per rules
    payloadFilters.category = serializeSelection(
      'category',
      filters.category
    );
    payloadFilters.subCategory = serializeSelection(
      'subCategory',
      filters.subCategory
    );
    payloadFilters.region = serializeSelection('region', filters.region);
    payloadFilters.sub_region = serializeSelection(
      'sub_region',
      filters.sub_region
    );
    payloadFilters.retailer = serializeSelection('retailer', filters.retailer);
    payloadFilters.city = serializeSelection('city', filters.city);
    payloadFilters.manufacturer = serializeSelection(
      'manufacturer',
      filters.manufacturer
    );
    payloadFilters.brand = serializeSelection('brand', filters.brand);
    payloadFilters.sub_brand = serializeSelection(
      'sub_brand',
      filters.sub_brand
    );
    payloadFilters.channel = serializeSelection('channel', filters.channel);
    payloadFilters.retailer_store = serializeSelection(
      'retailer_store',
      filters.retailer_store
    );
    payloadFilters.promo_flag = serializeSelection(
      'promo_flag',
      filters.promo_flag
    );
    payloadFilters.pack_group = serializeSelection(
      'pack_group',
      filters.pack_group
    );
    payloadFilters.pack_size_group = serializeSelection(
      'pack_size_group',
      filters.pack_size_group
    );
    payloadFilters.price_point = serializeSelection(
      'price_point',
      filters.price_point
    );
    payloadFilters.use_cache = filters.use_cache || 'true';

    const payload = {
      projName: title,
      time_dimension: 'L1W',
      filters: payloadFilters,
    };

    try {
      if (handleSummary2) {
        // await handleSummary2(payload);
        const apiResponse = await handleSummary2(payload);

        const taskId =
          // apiResponse?.Task_id ||
          apiResponse?.task_id ||
          apiResponse?.data?.task_id ||
          apiResponse?.result?.task?.id ||
          null;
        callLogEvent(
          'COMPLETED_GENERATE_AI_INSIGHT',
          `Task ID: ${taskId || 'N/A'}, Filters: ${JSON.stringify(filters)}`
        );
      } else {
      }
    } catch (error) {
      console.error('Error caught in MessageList:', error);
    } finally {
      setIsSummaryGenerating(false);
    }
  };

  // Keep retailer selection in sync when selected regions change (run outside render)
  useEffect(() => {
    if (!regionRetailerMap) return;
    const selectedRegions = (filters.region as string[]) || [];
    if (!selectedRegions.length) return;

    const allowed = new Set<string>();
    selectedRegions.forEach((r) => {
      const s = regionRetailerMap[r];
      if (s) s.forEach((ret) => allowed.add(ret));
    });

    setFilters((prev: FiltersState) => {
      const currentRetailers = prev.retailer || [];
      const filtered = currentRetailers.filter((r) => allowed.has(r));
      if (filtered.length === currentRetailers.length) return prev;
      return { ...prev, retailer: filtered };
    });
  }, [filters.region, regionRetailerMap]);

  // When filters change, compute valid option sets from master rows and deselect selections
  // that are no longer valid. NOTE: this effect does NOT modify `updatedFilters` (dropdown
  // option lists remain intact); it only un-checks invalid selections in `filters`.
  useEffect(() => {
    const safe = (v: any) => {
      if (v === null || typeof v === 'undefined') return null;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'string') return v.trim();
      return String(v);
    };

    if (!masterGeoData && !masterProdData) return;

    const regionSet = new Set<string>();
    const subRegionSet = new Set<string>();
    const retailerSet = new Set<string>();
    const citySet = new Set<string>();
    const channelSet = new Set<string>();
    const retailerStoreSet = new Set<string>();

    const brandSet = new Set<string>();
    const subBrandSet = new Set<string>();
    const manufacturerSet = new Set<string>();
    const packGroupSet = new Set<string>();
    const packSizeSet = new Set<string>();
    const pricePointSet = new Set<string>();

    const geoRowMatches = (row: any) => {
      const rRegion = safe(row.region);
      const rSubRegion = safe(row.sub_region);
      const rRetailer = safe(row.retailer);
      const rCity = safe(row.city);
      const rChannel = safe(row.channel);
      const rStore = safe(row.retailer_store_name);

      if (filters.region && filters.region.length) {
        if (!rRegion || !filters.region.includes(rRegion)) return false;
      }
      if (filters.sub_region && filters.sub_region.length) {
        if (!rSubRegion || !filters.sub_region.includes(rSubRegion))
          return false;
      }
      if (filters.retailer && filters.retailer.length) {
        if (!rRetailer || !filters.retailer.includes(rRetailer)) return false;
      }
      if (filters.city && filters.city.length) {
        if (!rCity || !filters.city.includes(rCity)) return false;
      }
      if (filters.channel && filters.channel.length) {
        if (!rChannel || !filters.channel.includes(rChannel)) return false;
      }
      if (filters.retailer_store && filters.retailer_store.length) {
        if (!rStore || !filters.retailer_store.includes(rStore)) return false;
      }
      return true;
    };

    const prodRowMatches = (row: any) => {
      const rBrand = safe(row.brand);
      const rSubBrand = safe(row.sub_brand);
      const rManufacturer = safe(row.manufacturer);
      const rPackGroup = safe(row.pack_group);
      const rPackSize = safe(row.pack_size_group);
      const rPricePoint = safe(row.price_point);

      if (filters.brand && filters.brand.length) {
        if (!rBrand || !filters.brand.includes(rBrand)) return false;
      }
      if (filters.sub_brand && filters.sub_brand.length) {
        if (!rSubBrand || !filters.sub_brand.includes(rSubBrand)) return false;
      }
      if (filters.manufacturer && filters.manufacturer.length) {
        if (!rManufacturer || !filters.manufacturer.includes(rManufacturer))
          return false;
      }
      if (filters.pack_group && filters.pack_group.length) {
        if (!rPackGroup || !filters.pack_group.includes(rPackGroup))
          return false;
      }
      if (filters.pack_size_group && filters.pack_size_group.length) {
        if (!rPackSize || !filters.pack_size_group.includes(rPackSize)) return false;
      }
      if (filters.price_point && filters.price_point.length) {
        if (!rPricePoint || !filters.price_point.includes(rPricePoint))
          return false;
      }
      return true;
    };

    const geoMatches: any[] = [];
    const prodMatches: any[] = [];

    if (masterGeoData && Array.isArray(masterGeoData)) {
      masterGeoData.forEach((row) => {
        if (geoRowMatches(row)) geoMatches.push(row);
      });
    }
    if (masterProdData && Array.isArray(masterProdData)) {
      masterProdData.forEach((row) => {
        if (prodRowMatches(row)) prodMatches.push(row);
      });
    }

    let finalGeoRows = geoMatches;
    let finalProdRows = prodMatches;

    if (geoMatches.length && prodMatches.length) {
      const prodRetailerSet = new Set(
        prodMatches.map((p) => safe(p.retailer)).filter(Boolean)
      );
      if (prodRetailerSet.size > 0) {
        finalGeoRows = geoMatches.filter((g) =>
          prodRetailerSet.has(safe(g.retailer))
        );
        const geoRetailerSet = new Set(
          finalGeoRows.map((g) => safe(g.retailer)).filter(Boolean)
        );
        finalProdRows = prodMatches.filter((p) =>
          geoRetailerSet.has(safe(p.retailer))
        );
      }
    }

    // Build per-category option sets while ignoring the category's own selection so
    // selecting one option doesn't immediately remove other options from the same dropdown.
    // Also preserve cross-scoping between geo and prod via retailer when both sides exist.
    const prodRetailerSet = new Set(
      finalProdRows.map((p) => safe(p.retailer)).filter(Boolean)
    );
    const geoRetailerSet = new Set(
      finalGeoRows.map((g) => safe(g.retailer)).filter(Boolean)
    );

    const geoMatchesIgnoring = (ignoreKey: string, row: any) => {
      const rRegion = safe(row.region);
      const rSubRegion = safe(row.sub_region);
      const rRetailer = safe(row.retailer);
      const rCity = safe(row.city);
      const rChannel = safe(row.channel);
      const rStore = safe(row.retailer_store_name);

      if (ignoreKey !== 'region' && filters.region && filters.region.length) {
        if (!rRegion || !filters.region.includes(rRegion)) return false;
      }
      if (
        ignoreKey !== 'sub_region' &&
        filters.sub_region &&
        filters.sub_region.length
      ) {
        if (!rSubRegion || !filters.sub_region.includes(rSubRegion))
          return false;
      }
      if (
        ignoreKey !== 'retailer' &&
        filters.retailer &&
        filters.retailer.length
      ) {
        if (!rRetailer || !filters.retailer.includes(rRetailer)) return false;
      }
      if (ignoreKey !== 'city' && filters.city && filters.city.length) {
        if (!rCity || !filters.city.includes(rCity)) return false;
      }
      if (
        ignoreKey !== 'channel' &&
        filters.channel &&
        filters.channel.length
      ) {
        if (!rChannel || !filters.channel.includes(rChannel)) return false;
      }
      if (
        ignoreKey !== 'retailer_store' &&
        filters.retailer_store &&
        filters.retailer_store.length
      ) {
        if (!rStore || !filters.retailer_store.includes(rStore)) return false;
      }

      // If prod side exists, ensure this geo row has at least one product retailer match
      if (prodMatches.length && prodRetailerSet.size) {
        if (!rRetailer || !prodRetailerSet.has(rRetailer)) return false;
      }

      return true;
    };

    const prodMatchesIgnoring = (ignoreKey: string, row: any) => {
      const rBrand = safe(row.brand);
      const rSubBrand = safe(row.sub_brand);
      const rManufacturer = safe(row.manufacturer);
      const rPackGroup = safe(row.pack_group);
      const rPackSize = safe(row.pack_size_group);
      const rPricePoint = safe(row.price_point);
      const rRetailer = safe(row.retailer);

      if (ignoreKey !== 'brand' && filters.brand && filters.brand.length) {
        if (!rBrand || !filters.brand.includes(rBrand)) return false;
      }
      if (
        ignoreKey !== 'sub_brand' &&
        filters.sub_brand &&
        filters.sub_brand.length
      ) {
        if (!rSubBrand || !filters.sub_brand.includes(rSubBrand)) return false;
      }
      if (
        ignoreKey !== 'manufacturer' &&
        filters.manufacturer &&
        filters.manufacturer.length
      ) {
        if (!rManufacturer || !filters.manufacturer.includes(rManufacturer))
          return false;
      }
      if (
        ignoreKey !== 'pack_group' &&
        filters.pack_group &&
        filters.pack_group.length
      ) {
        if (!rPackGroup || !filters.pack_group.includes(rPackGroup))
          return false;
      }
      if (
        ignoreKey !== 'pack_size_group' &&
        filters.pack_size_group &&
        filters.pack_size_group.length
      ) {
        if (!rPackSize || !filters.pack_size_group.includes(rPackSize)) return false;
      }
      if (
        ignoreKey !== 'price_point' &&
        filters.price_point &&
        filters.price_point.length
      ) {
        if (!rPricePoint || !filters.price_point.includes(rPricePoint))
          return false;
      }

      // If geo side exists, ensure this prod row has at least one geo retailer match
      if (geoMatches.length && geoRetailerSet.size) {
        if (!rRetailer || !geoRetailerSet.has(rRetailer)) return false;
      }

      return true;
    };

    // Build geo sets by scanning masterGeoData and ignoring each geo category in turn
    if (masterGeoData && Array.isArray(masterGeoData)) {
      masterGeoData.forEach((row) => {
        if (geoMatchesIgnoring('region', row)) {
          const v = safe(row.region);
          if (v) regionSet.add(v);
        }
        if (geoMatchesIgnoring('sub_region', row)) {
          const v = safe(row.sub_region);
          if (v) subRegionSet.add(v);
        }
        if (geoMatchesIgnoring('retailer', row)) {
          const v = safe(row.retailer);
          if (v) retailerSet.add(v);
        }
        if (geoMatchesIgnoring('city', row)) {
          const v = safe(row.city);
          if (v) citySet.add(v);
        }
        if (geoMatchesIgnoring('channel', row)) {
          const v = safe(row.channel);
          if (v) channelSet.add(v);
        }
        if (geoMatchesIgnoring('retailer_store', row)) {
          const v = safe(row.retailer_store_name);
          if (v) retailerStoreSet.add(v);
        }
      });
    }

    // Build product sets by scanning final product rows (scoped by geo when available)
    // and ensure cross-scoping between product categories. We compute each
    // category's available values by checking whether there exists a product row
    // that matches the current selections of all OTHER product categories and
    // (when geo is present) also matches the geo-scoped retailers.
    const prodSourceRows: any[] =
      finalProdRows && finalProdRows.length
        ? finalProdRows
        : Array.isArray(masterProdData)
          ? masterProdData
          : [];

    const prodCatKeys = [
      { key: 'brand', prop: 'brand', set: brandSet },
      { key: 'sub_brand', prop: 'sub_brand', set: subBrandSet },
      { key: 'manufacturer', prop: 'manufacturer', set: manufacturerSet },
      { key: 'pack_group', prop: 'pack_group', set: packGroupSet },
      { key: 'pack_size_group', prop: 'pack_size_group', set: packSizeSet },
      { key: 'price_point', prop: 'price_point', set: pricePointSet },
    ];

    // For quick geo-retailer lookup when both sides exist
    const geoRetailersAllowed = new Set(
      finalGeoRows.map((g) => safe(g.retailer)).filter(Boolean)
    );

    prodSourceRows.forEach((row) => {
      // skip rows that don't match geo scope when both geo & prod present
      if (finalGeoRows && finalGeoRows.length) {
        const rRetailer = safe(row.retailer);
        if (
          geoRetailersAllowed.size &&
          (!rRetailer || !geoRetailersAllowed.has(rRetailer))
        )
          return;
      }

      prodCatKeys.forEach((cat) => {
        const value = safe(row[cat.prop]);
        if (!value) return;

        // Check if this row satisfies selections in all other product categories
        let ok = true;
        prodCatKeys.forEach((other) => {
          if (other.key === cat.key) return; // ignore self
          const sel = (filters as any)[other.key] || [];
          if (sel && sel.length) {
            const rowVal = safe(row[other.prop]);
            if (!rowVal || !sel.includes(rowVal)) {
              ok = false;
            }
          }
        });

        if (ok) {
          cat.set.add(value);
        }
      });
    });

    // Build updatedFilters object from these sets so dropdowns only show valid options
    setUpdatedFilters((prev) => {
      const base = prev ? { ...prev } : { ...(filterOptions as any) };
      const updated: any = { ...base };

      const toOptionObject = (arr: string[]) => {
        const sorted = [...arr].sort((a, b) =>
          String(a).toLowerCase().localeCompare(String(b).toLowerCase())
        );
        return {
          ALL: 'ALL',
          ...Object.fromEntries(sorted.map((v: any) => [v, v])),
        };
      };

      if (regionSet.size)
        updated['Region'] = toOptionObject(Array.from(regionSet));
      if (subRegionSet.size)
        updated['Sub Region'] = toOptionObject(Array.from(subRegionSet));
      if (retailerSet.size)
        updated['Retailer'] = toOptionObject(Array.from(retailerSet));
      if (citySet.size)
        updated['City'] = toOptionObject(Array.from(citySet));
      if (channelSet.size)
        updated['Channel'] = toOptionObject(Array.from(channelSet));
      if (retailerStoreSet.size)
        updated['Retailer Store'] = toOptionObject(
          Array.from(retailerStoreSet)
        );

      // Only replace product group dropdowns if masterProdData or prodSourceRows are available
      const hasProductData =
        (prodSourceRows && prodSourceRows.length) ||
        (masterProdData && masterProdData.length);
      if (hasProductData) {
        // For each product filter, cross-scope by all other product filters
        const productKeys = [
          { key: 'brand', label: 'Brand' },
          { key: 'sub_brand', label: 'Sub Brand' },
          { key: 'manufacturer', label: 'Manufacturer' },
          { key: 'pack_group', label: 'Pack Group' },
          { key: 'pack_size_group', label: 'Pack Size' },
          { key: 'price_point', label: 'Price Point' },
        ];
        productKeys.forEach(({ key, label }) => {
          // For this filter, filter masterProdData by all other product filters except itself
          const filteredRows = (masterProdData || []).filter((row) => {
            return productKeys.every(({ key: otherKey }) => {
              if (otherKey === key) return true; // Ignore own selection
              const sel = (filters as any)[otherKey];
              if (sel && sel.length > 0) {
                return sel.includes(String((row as any)[otherKey]));
              }
              return true;
            });
          });
          const validSet = new Set<string>();
          filteredRows.forEach((row) => {
            if (row[key] != null) {
              validSet.add(String(row[key]));
            }
          });
          // Note: do NOT implicitly re-add current selections here. The parent
          // should be authoritative about which options remain visible. Removing
          // this automatic-preservation avoids leaving invalid selections visible
          // when cross-scoping removes their supporting rows.
          if (validSet.size) {
            updated[label] = toOptionObject(Array.from(validSet) as string[]);
          }
        });
      }

      return updated;
    });

    // Now validate product selections against combined geo+prod constraints
    // and remove any selected values that are no longer valid. We intentionally
    // only modify product selections here (not geo selections). Promo flag is
    // not part of the cross-product grouping and therefore is not auto-updated.
    const localScopedFlags: Record<string, boolean> = {};

    // Product filter keys to validate (do NOT include promo_flag here)
    const productKeys = [
      'manufacturer',
      'brand',
      'sub_brand',
      'pack_group',
      'pack_size_group',
      'price_point',
    ];

    // finalProdRows is already geo-scoped when possible. Use it as the source
    // of truth for valid product values.
    const cleaned: Partial<FiltersState> = {};
    let needsUpdate = false;

    productKeys.forEach((key) => {
      const valid = new Set<string>();

      finalProdRows.forEach((row) => {
        // Check this row satisfies all OTHER product selections (ignore the key itself)
        let ok = true;
        productKeys.forEach((other) => {
          if (other === key) return;
          const sel = (filters as any)[other] || [];
          if (sel && sel.length) {
            const rowVal = safe(row[other]);
            if (!rowVal || !sel.includes(rowVal)) {
              ok = false;
            }
          }
        });
        if (!ok) return;

        const v = safe(row[key]);
        if (v) valid.add(v);
      });
      // Ensure we don't remove values the user already selected for this same
      try {
        const currentSel = (filters as any)[key] || [];
        if (Array.isArray(currentSel) && currentSel.length) {
          currentSel.forEach((s: any) => {
            if (s != null && String(s) !== 'ALL') valid.add(String(s));
          });
        }
      } catch (e) {
        // ignore
      }

      const curr = ((filters as any)[key] || []) as string[];
      const hadAllMarker = curr.includes('ALL');
      const effectiveCurr = hadAllMarker ? Array.from(valid) : curr.slice();
      const newSel = effectiveCurr.filter((v) => valid.has(v));

      if (newSel.length !== curr.length) {
        (cleaned as any)[key] = newSel;
        needsUpdate = true;
        localScopedFlags[key] = false;
      }
    });

    if (needsUpdate) {
      setFilters((prev) => ({ ...prev, ...cleaned }));
    }

    // Update scoped flags visible to the UI
    setScopedAllFlags((prev) => ({ ...prev, ...localScopedFlags }));
  }, [filters, masterGeoData, masterProdData]);

  const handleFeedback = async (data: any, index: number) => {
    messages[index]['is_liked'] = !messages[index]['is_liked'];
    const req = {
      email: currentUserEmail,
      request_id: data.request_id,
      is_liked: messages[index]['is_liked'],
    };
    handleChange && handleChange(messages);
    setShowSuccessMsg(true);
    setTimeout(() => {
      setShowSuccessMsg(false);
    }, 5000);
    saveLikeMessage(req);
    let extraInfoLike = '';
    try {
      let queryText = '';
      if (data && typeof data.question === 'string' && data.question.trim()) {
        queryText = data.question.trim();
      } else if (
        questionRefs &&
        questionRefs.current &&
        questionRefs.current[index]
      ) {
        const qEl: any = questionRefs.current[index];
        if (qEl && (qEl.textContent || qEl.innerText)) {
          queryText = (qEl.textContent || qEl.innerText).trim();
        }
      }

      if (queryText) {
        try {
          const renderedQ = await Promise.resolve(
            marked.parse(queryText || '')
          );
          const tmpQ = document.createElement('div');
          tmpQ.innerHTML =
            typeof renderedQ === 'string' ? renderedQ : String(renderedQ);
          queryText = (tmpQ.textContent || tmpQ.innerText || '').trim();
        } catch (e) {
          queryText = queryText
            .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
            .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
            .replace(/(```[\s\S]*?```|`[^`]*`)/g, '')
            .replace(/[>#*_~]/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }
        if (queryText.length > 200)
          queryText = queryText.substring(0, 200) + '...';
        extraInfoLike = ` | Query: ${queryText}`;
      } else {
        let summaryText = '';
        if (data) {
          if (typeof data.chatMessage === 'string' && data.chatMessage.trim()) {
            summaryText = data.chatMessage.trim();
          } else if (typeof data.content === 'string' && data.content.trim()) {
            summaryText = data.content.trim();
          } else if (data.summary && typeof data.summary === 'string') {
            summaryText = data.summary.trim();
          }
        }
        let firstSentence = '';
        if (summaryText) {
          const m = summaryText.match(/^[\s\S]*?[\.?!](?=\s|$)/);
          if (m && m[0]) firstSentence = m[0].trim();
          else firstSentence = summaryText.split('\n')[0].trim();
          firstSentence = firstSentence.replace(/\s+/g, ' ');
          try {
            const rendered = await Promise.resolve(
              marked.parse(firstSentence || '')
            );
            const tmp = document.createElement('div');
            tmp.innerHTML =
              typeof rendered === 'string' ? rendered : String(rendered);
            firstSentence = (tmp.textContent || tmp.innerText || '').trim();
          } catch (e) {
            firstSentence = firstSentence
              .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
              .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
              .replace(/(```[\s\S]*?```|`[^`]*`)/g, '')
              .replace(/[>#*_~]/g, '')
              .replace(/<[^>]*>/g, '')
              .replace(/\s+/g, ' ')
              .trim();
          }
          if (firstSentence.length > 200)
            firstSentence = firstSentence.substring(0, 200) + '...';
          extraInfoLike = ` | Filters: ${firstSentence}`;
        }
      }
    } catch (e) {
      // ignore logging extras on error
    }

    callLogEvent(
      'CLICKED_NEW_DIALOG_MESSAGE_LIKE_BUTTON',
      'Clicked Like message button' + extraInfoLike
    );
  };

  // Build the same extra-info string used for logging (prefer query, else first sentence of summary)
  const getExtraInfoFromMessage = async (data: any, index: number) => {
    try {
      let extra = '';
      let queryText = '';
      if (data && typeof data.question === 'string' && data.question.trim()) {
        queryText = data.question.trim();
      } else if (
        questionRefs &&
        questionRefs.current &&
        questionRefs.current[index]
      ) {
        const qEl: any = questionRefs.current[index];
        if (qEl && (qEl.textContent || qEl.innerText)) {
          queryText = (qEl.textContent || qEl.innerText).trim();
        }
      }

      if (queryText) {
        try {
          const renderedQ = await Promise.resolve(
            marked.parse(queryText || '')
          );
          const tmpQ = document.createElement('div');
          tmpQ.innerHTML =
            typeof renderedQ === 'string' ? renderedQ : String(renderedQ);
          queryText = (tmpQ.textContent || tmpQ.innerText || '').trim();
        } catch (e) {
          queryText = queryText
            .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
            .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
            .replace(/(```[\s\S]*?```|`[^`]*`)/g, '')
            .replace(/[>#*_~]/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }
        if (queryText.length > 200)
          queryText = queryText.substring(0, 200) + '...';
        extra = ` | Query: ${queryText}`;
        return extra;
      }

      let summaryText = '';
      if (data) {
        if (typeof data.chatMessage === 'string' && data.chatMessage.trim()) {
          summaryText = data.chatMessage.trim();
        } else if (typeof data.content === 'string' && data.content.trim()) {
          summaryText = data.content.trim();
        } else if (data.summary && typeof data.summary === 'string') {
          summaryText = data.summary.trim();
        }
      }
      let firstSentence = '';
      if (summaryText) {
        const m = summaryText.match(/^[\s\S]*?[\.?!](?=\s|$)/);
        if (m && m[0]) firstSentence = m[0].trim();
        else firstSentence = summaryText.split('\n')[0].trim();
        firstSentence = firstSentence.replace(/\s+/g, ' ');
        try {
          const rendered = await Promise.resolve(
            marked.parse(firstSentence || '')
          );
          const tmp = document.createElement('div');
          tmp.innerHTML =
            typeof rendered === 'string' ? rendered : String(rendered);
          firstSentence = (tmp.textContent || tmp.innerText || '').trim();
        } catch (e) {
          firstSentence = firstSentence
            .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
            .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
            .replace(/(```[\s\S]*?```|`[^`]*`)/g, '')
            .replace(/[>#*_~]/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }
        if (firstSentence.length > 200)
          firstSentence = firstSentence.substring(0, 200) + '...';
        extra = ` | Filters: ${firstSentence}`;
      }
      return extra;
    } catch (e) {
      return '';
    }
  };

  const handleSaveChat = async (data: any, index: number) => {
    messages[index]['is_saved'] = !messages[index]['is_saved'];
    handleChange && handleChange(messages);
    if (messages[index]['is_saved']) {
      const req = {
        email: currentUserEmail,
        request_id: data.request_id,
      };
      saveChat(req);
      setShowSavedSuccessMsg('saved');
    } else {
      const req = {
        email: currentUserEmail,
        request_id: [data.request_id],
      };
      deleteSavedChats(req);
      setShowSavedSuccessMsg('removed');
    }

    setTimeout(() => {
      setShowSavedSuccessMsg('');
    }, 5000);
  };
  // Helper to extract message content from message
  const getMessageContent = (message: any): string => {
    try {
      const raw = message?.chatMessage || '';
      const lines = raw
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l);
      let content = '';
      if (lines.length >= 2) {
        content = `${lines[0]}\n${lines[1]}`;
      } else if (lines.length === 1) {
        content = lines[0];
      } else {
        content = raw.slice(0, 300);
      }
      // Remove HTML tags
      content = content.replace(/<[^>]*>/g, '');
      return content;
    } catch (e) {
      return '';
    }
  };

  // Helper to extract filter info from message
  const getFilterInfo = (message: any): string => {
    try {
      const filters: string[] = [];
      if (message?.filters && typeof message.filters === 'object') {
        Object.entries(message.filters).forEach(
          ([key, value]: [string, any]) => {
            if (value && (Array.isArray(value) ? value.length > 0 : true)) {
              filters.push(
                `${key}:${Array.isArray(value) ? value.join(',') : value}`
              );
            }
          }
        );
      }
      return filters.slice(0, 3).join('|') || 'no-filters';
    } catch (e) {
      return 'no-filters';
    }
  };

  // Pin/unpin a summary in the messages list. Pinned summaries are moved to the top
  const handlePinToggle = (data: any, index: number) => {
    try {
      // If the parent provided a callback, use it (for when messages are filtered)
      if (onPinToggle && data?.request_id) {
        onPinToggle(data.request_id, !data?.is_pinned);
        return;
      }

      // Otherwise, operate on the local messages array (backward compatibility)
      const updated = [...messages];
      // Find the actual index of this message in the array
      const actualIndex = updated.findIndex(
        (msg: any) => msg === data || msg?.request_id === data?.request_id
      );
      if (actualIndex === -1) {
        console.warn('Message not found in messages array');
        return;
      }

      const current = updated[actualIndex];
      const messageContent = getMessageContent(current);
      const filterInfo = getFilterInfo(current);
      const action = !current.is_pinned ? 'PIN' : 'UNPIN';

      // Log the event with message content and filters
      callLogEvent(
        `MESSAGE_${action}_CLICKED`,
        `User ${action} message - Content: ${messageContent} `
      );

      current.is_pinned = !current.is_pinned;
      // move pinned item to beginning if pinned, otherwise keep relative order
      if (current.is_pinned) {
        updated.splice(actualIndex, 1);
        updated.unshift(current);
      } else {
        // unpin: simply toggle the flag (we leave ordering as-is to avoid complexity)
        updated[actualIndex] = current;
      }
      handleChange && handleChange(updated);
    } catch (e) {
      console.error('Failed to toggle pin', e);
    }
  };

  const handleDeleteSummary = (data: any, index: number) => {
    try {
      // If the parent provided a callback, use it (for when messages are filtered)
      // Parent will handle logging to avoid duplicate logs
      if (onDeleteMessage && data?.request_id) {
        onDeleteMessage(data.request_id);
        return;
      }

      // Otherwise, operate on the local messages array (backward compatibility)
      // No logging here - logging happens in parent handler only
      const updated = [...messages];
      // Find the actual index of this message in the array
      const actualIndex = updated.findIndex(
        (msg: any) => msg === data || msg?.request_id === data?.request_id
      );
      if (actualIndex === -1) {
        console.warn('Message not found in messages array');
        return;
      }

      if (updated.length === 1) {
        updated[0] = { sender: '_hidden' };
      } else {
        updated.splice(actualIndex, 1);
      }
      handleChange && handleChange(updated);
    } catch (e) {
      console.error('Failed to remove summary locally', e);
    }
  };
  const handleFeedbackReason = (data: any, index: number) => {
    setCurrentRecordIndex(index);
    setShowFeedbackForm(true);
  };
  const submitFeedbackReason = async (data: any) => {
    let extra = '';
    try {
      if (
        typeof currentRecordIndex !== 'undefined' &&
        currentRecordIndex !== null &&
        messages[currentRecordIndex]
      ) {
        extra = await getExtraInfoFromMessage(
          messages[currentRecordIndex],
          currentRecordIndex
        );
      }
    } catch (e) {
      extra = '';
    }

    callLogEvent(
      'CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON_FEEDBACKFORM_SUBMIT',
      'Submitted Feedback form post dislike button click with comments: ' +
      JSON.stringify(data) +
      (extra || '')
    );
    setShowFeedbackForm(true);
    const currentRecord = messages[currentRecordIndex];
    const req = {
      chat_id: 0,
      old_dialog: true,
      request_id: currentRecord.request_id,
      email: currentUserEmail,
      accurate_ans: data.accurate_ans,
      correct_format: data.correct_format,
      correct_sql: data.correct_sql,
      expected_format: data.expected_format,
      other_comments: data.other_comments,
      hash: currentRecord.hash,
    };
    if (messages[currentRecordIndex]['is_liked']) {
      messages[currentRecordIndex]['is_liked'] =
        !messages[currentRecordIndex]['is_liked'];
    }
    messages[currentRecordIndex]['is_disliked'] =
      !messages[currentRecordIndex]['is_disliked'];
    handleChange && handleChange(messages);
    setShowSuccessMsg(true);
    setTimeout(() => {
      setShowSuccessMsg(false);
    }, 5000);
    setShowFeedbackForm(false);
    saveFeedback(req);
  };
  const handleCharts = (msg: any) => {
    onSend(msg);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');

  // derive rendered messages once per messages update to avoid repeated large-list filtering
  const filteredMessages = useMemo(
    () =>
      (messages || []).filter(
        (m: any) => !(m?.sender === '_hidden' || m?.is_deleted)
      ),
    [messages]
  );

  // compute latest summary index once per filtered list update
  const latestSummaryIndex = useMemo(() => {
    let idx = -1;
    filteredMessages.forEach((m: any, i: number) => {
      if (m?.sender === 'summary') idx = i;
    });
    return idx;
  }, [filteredMessages]);

  const getSummaryOrdinal = (messageArrayIndex: number) => {
    if (!Array.isArray(filteredMessages) || messageArrayIndex < 0) return 0;
    return filteredMessages
      .slice(0, messageArrayIndex + 1)
      .filter((m: any) => m?.sender === 'summary').length;
  };

  const summaryHistoryItems = useMemo(() => {
    return filteredMessages
      .map((msg: any, idx: number) => ({ msg, idx }))
      .filter(({ msg }: any) => msg?.sender === 'summary' && !msg?.is_deleted);
  }, [filteredMessages]);

  const activeSummaryIndex =
    summaryIndex >= 0 ? summaryIndex : latestSummaryIndex;

  const insightSections = [
    { label: 'PERFORMANCE TREE', displayLabel: 'Performance Tree', icon: AccountTreeOutlinedIcon },
    { label: 'KEY TAKEAWAYS', displayLabel: 'Key Takeaways', icon: InsightsOutlinedIcon },
    { label: 'OVERALL INSIGHTS', displayLabel: 'Overall Insights', icon: AnalyticsOutlinedIcon },
    { label: 'REGIONAL INSIGHTS', displayLabel: 'Regional Insights', icon: PlaceOutlinedIcon },
    { label: 'RETAILER INSIGHTS', displayLabel: 'Retailer Insights', icon: StorefrontOutlinedIcon },
    { label: 'PROMO INSIGHTS', displayLabel: 'Promo Insights', icon: LocalOfferOutlinedIcon },
    { label: 'PEPSICO INSIGHTS', displayLabel: 'PepsiCo Insights', icon: Inventory2OutlinedIcon },
    { label: 'COMPETITOR INSIGHTS', displayLabel: 'Competitor Insights', icon: GroupsOutlinedIcon },
    { label: 'PRICE POINT INSIGHTS', displayLabel: 'Price Point Insights', icon: AttachMoneyOutlinedIcon },
    { label: 'MARKET INSIGHTS', displayLabel: 'Market Insights', icon: AssessmentOutlinedIcon },
    { label: 'SUMMARISED INSIGHTS', displayLabel: 'Summarised Insights', icon: AutoAwesomeOutlinedIcon },
  ];

  const normalizeInsightLabel = (value: string) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

  const extractAvailableInsightLabels = (chatMessage: string): string[] => {
    if (!chatMessage) return [];

    const availableLabels = new Set<string>();
    const detailsRegex = /<details[^>]*>\s*<summary[^>]*>([\s\S]*?)<\/summary>\s*([\s\S]*?)<\/details>/gi;
    let match: RegExpExecArray | null;

    while ((match = detailsRegex.exec(chatMessage)) !== null) {
      const rawSummary = (match[1] || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const rawBody = (match[2] || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      // Only skip if there's no summary; allow empty bodies for sections like Summarised Insights
      if (!rawSummary) continue;

      const normalizedSummary = normalizeInsightLabel(rawSummary);
      insightSections.forEach(({ label }) => {
        if (normalizedSummary === label || normalizedSummary.includes(label)) {
          availableLabels.add(label);
        }
      });
    }

    return insightSections
      .map(({ label }) => label)
      .filter((label) => availableLabels.has(label));
  };

  const activeSummaryMessage = useMemo(() => {
    if (activeSummaryIndex >= 0 && filteredMessages[activeSummaryIndex]?.sender === 'summary') {
      return filteredMessages[activeSummaryIndex];
    }
    if (latestSummaryIndex >= 0 && filteredMessages[latestSummaryIndex]?.sender === 'summary') {
      return filteredMessages[latestSummaryIndex];
    }
    return null;
  }, [filteredMessages, activeSummaryIndex, latestSummaryIndex]);

  useEffect(() => {
    if (!onSummaryContextChange) return;
    onSummaryContextChange({
      activeInsightSection: activeInsightSection || null,
      activeSummaryMessage: activeSummaryMessage || null,
    });
  }, [activeInsightSection, activeSummaryMessage, onSummaryContextChange]);

  const visibleInsightSections = useMemo(() => {
    if (!isMiddleEastMarket) return insightSections;
    if (activeSummaryMessage?.isError) return [];
    const availableLabels = extractAvailableInsightLabels(
      String(activeSummaryMessage?.chatMessage || '')
    );
    if (!availableLabels.length) return insightSections;
    // Always include PERFORMANCE TREE even if not in summary HTML
    return insightSections.filter(({ label }) => label === 'PERFORMANCE TREE' || availableLabels.includes(label));
  }, [isMiddleEastMarket, activeSummaryMessage]);

  const effectiveActiveInsightSection = useMemo(() => {
    if (!visibleInsightSections.length) return 'PERFORMANCE TREE';
    if (
      activeInsightSection &&
      visibleInsightSections.some(({ label }) => label === activeInsightSection)
    ) {
      return activeInsightSection;
    }
    return visibleInsightSections[0].label;
  }, [visibleInsightSections, activeInsightSection]);

  useEffect(() => {
    if (!isMiddleEastMarket) return;
    if (!visibleInsightSections.length) return;
    if (activeInsightSection !== effectiveActiveInsightSection) {
      setActiveInsightSection(effectiveActiveInsightSection);
    }
  }, [
    isMiddleEastMarket,
    visibleInsightSections,
    activeInsightSection,
    effectiveActiveInsightSection,
    setActiveInsightSection,
  ]);

  // Update Redux treeInfo when the active summary changes (e.g. via History)
  useEffect(() => {
    if (!isMiddleEastMarket) return;
    if (uiMode !== 'summary') return;
    const treeData = activeSummaryMessage?.treeInfo ?? null;
    dispatch(updateTreeInfo(treeData));
  }, [activeSummaryMessage, isMiddleEastMarket, dispatch, uiMode]);

  const getHistoryPreviewText = (msg: any) => {
    const raw = String(msg?.chatMessage || '');
    const introBlockMatch = raw.match(/^[\s\S]*?(?=<details\b)/i);
    const introBlock = introBlockMatch?.[0] || raw;
    const clean = introBlock
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/<br\s*\/?\s*>/gi, ' ')
      .replace(/<summary[^>]*>[\s\S]*?<\/summary>/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return clean;
  };

  return (
    <>
      <Snackbar
        open={showSuccessMsg || showSavedSuccessMsg !== ''}
        ContentProps={{
          sx: {
            background: 'green',
          },
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        message={
          showSuccessMsg
            ? 'Thanks for your feedback'
            : showSavedSuccessMsg === 'saved'
              ? 'Chat saved successfully'
              : 'Chat not saved'
        }
        key={'liked'}
      />
      <div
        className={styles.pageWrapper}
        style={
          uiMode === 'summary'
            ? {
              background: 'transparent',
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
              padding: 0,
              ...(reduxTitle === 'UAE MT'
                ? {
                  // zoom: '90%',
                  width: '100%',
                  transformOrigin: 'top left',
                }
                : {}),
            }
            : undefined
        }
      >
        <div className={styles.stickyContainer}>
          {/* {reduxTitle === 'UAE MT' && (
            <div className={styles.topFilterContainer}>
                {getFilterStyles2(true)}
            </div>
        )} */}
          {uiMode === 'summary' &&
            isMiddleEastMarket &&
            !propSetActiveInsightSection &&
            !propSetIsInsightsNavOpen && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2.5,
                pt: 0.5,
              }}
            >
              <Box sx={{ minWidth: 44, display: 'flex', alignItems: 'center' }}>
                {!isInsightsNavOpen && (
                  <IconButton
                    onClick={() => setIsInsightsNavOpen(true)}
                    aria-label='open-insights-navigation'
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '14px',
                      background: '#005bbd',
                      boxShadow: '0 8px 20px rgba(0, 91, 189, 0.22)',
                      color: '#fff',
                      '&:hover': {
                        background: '#004c9a',
                      },
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                )}
              </Box>

              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  marginTop: '6px',
                  marginLeft: 'auto',
                }}
              >
                <Button
                  variant='outlined'
                  onClick={() => {
                    setIsSummaryHistoryOpen(true);
                    callLogEvent(
                      'CLICKED_SUMMARY_HISTORY_BUTTON',
                      'Opened summary history drawer'
                    );
                  }}
                  startIcon={<HistoryIcon />}
                  sx={{
                    borderRadius: '10px',
                    height: '36px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2,
                    color: '#425466',
                    borderColor: '#d2dce8',
                    background: '#f6f9fc',
                    '&:hover': {
                      borderColor: '#b9c8da',
                      background: '#edf3f9',
                    },
                  }}
                >
                  History
                </Button>

                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    // Prevent opening the dialog while a summary is being generated or filters are syncing/disabled
                    if (isSummaryGenerating || syncFilters || disableSummaryBtn)
                      return;
                    // Open the filter dialog for both desktop and mobile (we removed mobile Drawer)
                    setIsFilterDrawerOpen(true);
                  }}
                  startIcon={<FilterAltIcon />}
                  sx={{
                    backgroundImage:
                      'linear-gradient(to right, #A084E8, #86A8E7)',
                    borderRadius: '10px',
                    height: '36px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    color: 'white',
                    textTransform: 'none',
                    padding: '0 20px',
                    boxShadow: '0 6px 16px rgba(134,168,231,0.34)',
                    '&:hover': {
                      backgroundImage:
                        'linear-gradient(to right, #9073D8, #7697D7)',
                      boxShadow: '0 8px 20px rgba(134,168,231,0.38)',
                    },
                  }}
                  disabled={
                    isSummaryGenerating || syncFilters || disableSummaryBtn
                  }
                >
                  {isSummaryGenerating || syncFilters || disableSummaryBtn ? (
                    <CircularProgress
                      size={18}
                      disableShrink
                      sx={{ color: 'white' }}
                    />
                  ) : (
                    'Generate Customized AI Insights'
                  )}
                </Button>

                <Tooltip
                  title='Help: Filters & Insights'
                  placement='top'
                >
                  <IconButton
                    onClick={() => {
                      setShowFilterHelp(true);
                      callLogEvent(
                        'CLICKED_FILTER_HELP_ICON',
                        'Clicked help icon (UAE MT Insights – Help & How-To Guide)'
                      );
                    }}
                    sx={{
                      backgroundImage:
                        'linear-gradient(90deg, #A084E8 0%, #86A8E7 100%)',
                      borderRadius: '6px',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      padding: 0,
                      '&:hover': {
                        backgroundImage:
                          'linear-gradient(90deg, #9073D8 0%, #7697D7 100%)',
                      },
                    }}
                    aria-label='filter-help-summary'
                  >
                    <InfoIcon sx={{ fontSize: 14, color: 'white' }} />
                  </IconButton>
                </Tooltip>
              </div>

              {/* Mobile Drawer removed: we use the dialog (full screen on mobile) */}
            </Box>
          )}
        </div>
        <div
          className={styles.scrollableContent}
          data-ui-mode={uiMode}
          ref={scrollContainerRef}
          onScroll={handleScrollableContainerScroll}
          style={
            uiMode === 'chat'
              ? {
                  paddingTop: tipVisible ? 18 : 8,
                  paddingBottom: tipVisible ? 120 : (!isIdeal ? 80 : 28),
                }
              : undefined
          }
        >
          {/* <ul className={styles.anchorul}>
				{userQuestions?.map((question: any, index: any) => (
					<li key={question.row_number}>
						<div
							onClick={() =>
								scrollToQuestion(question.row_number, question.chatMessage)
							}
							className={
								activeLink === question.row_number ? styles.activeLinkClass : ''
							}
							key={index}
						>
							<Tooltip
								title={question.chatMessage}
								placement={'left'}
							>
								{question.chatMessage}
							</Tooltip>
						</div>
					</li>
				))}
			</ul> */}
          <DWDModal
            show={showFeedbackForm}
            handleClose={() => setShowFeedbackForm(false)}
            handleChange={(e: any) => submitFeedbackReason(e)}
            modalName={'feedback'}
            // resData=''
          />
          {filteredMessages.map((msg: any, index: any) => (
            (() => {
              const shouldHideSummaryCard =
                uiMode === 'summary' &&
                isMiddleEastMarket &&
                msg?.sender === 'summary' &&
                Number(index) !== Number(activeSummaryIndex);

              if (shouldHideSummaryCard) {
                return null;
              }

              return (
            <div
              key={msg?.request_id || msg?.chatId || `${msg?.sender}-${index}`}
              className={styles.outerDiv}
              ref={(el) => (messageRefs.current[index] = el)}
              style={msg?.sender === 123 && uiMode === 'chat' && index > 0 ? { marginTop: 20 } : undefined}
            >
              {msg?.sender === 123 && uiMode === 'chat' ? (
                <>
                  <span className={styles.avatar}>
                    <HumanAvatarIcon
                      className={styles.chatBotSvg}
                      aria-hidden='true'
                    />
                  </span>
                  <span className={styles.sentMessage}>
                    <div
                      key={index}
                      ref={(el) => (questionRefs.current[index] = el)}
                    >
                      {msg.chatMessage}
                    </div>
                  </span>{' '}
                </>
              ) : (
                <>
                  {msg?.sender === 'summary' && uiMode === 'summary' ? (
                    <>
                      <div
                        className={styles.aiChatMessage}
                        ref={answerRef}
                      >
                        {/* <h5 style={{textDecoration:"underline", marginLeft:"50vh"}} hidden={reduxTitle.trim() != "UAE MT" }>UAE MT Insights Summary</h5> */}
                        {/* Placeholder: UAE MT specific top-summary content removed to restore balanced JSX. */}
                        {/* Always render the selected summary in expanded mode in the revamped UI. */}
                        {Number(index) === Number(activeSummaryIndex) ? (
                          <div
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              // marginBottom: 22,
                              display: 'flex',
                              flexDirection: 'column',
                              overflow: 'hidden',
                              height: 'calc(100vh - 100px)',
                              minHeight: 520,
                              position: 'relative',
                              // borderRadius: 16,
                              background: '#edf1f6',
                              // border: '1px solid #d7dee8',
                              ...(Number(index) === latestSummaryIndex
                                ? {
                                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                                  }
                                : {}),
                            }}
                          >
                            {/* Accordion content – full width below header */}
                            {!msg?.is_deleted && (
                              <div
                                style={{
                                  display: 'flex',
                                  width: '100%',
                                  flex: 1,
                                  minHeight: 0,
                                  // borderRadius: 16,
                                  overflow: 'hidden',
                                }}
                              >
                                {/* Inner sidebar for UAE/KSA: list of insights - only show if props not provided (i.e., using local state) */}
                                {isMiddleEastMarket && !propSetActiveInsightSection && isInsightsNavOpen && !isMobile && (
                                  <div
                                    style={{
                                      position: 'relative',
                                      width: 250,
                                      minWidth: 250,
                                      maxWidth: 250,
                                      flex: '0 0 250px',
                                      height: '100%',
                                      overflowY: 'hidden',
                                      overflowX: 'hidden',
                                      borderRight: '1px solid #d9e1eb',
                                      padding: 0,
                                      background: '#ffffff',
                                      zIndex: 10,
                                    }}
                                  >
                                    {visibleInsightSections.map(({ label, displayLabel, icon: ItemIcon }, itemIndex) => (
                                      <button
                                        key={label}
                                        onClick={() => {
                                          setActiveInsightSection(label);
                                          callLogEvent('CLICKED_INSIGHT_SECTION', `Insight section clicked: ${label}`);
                                        }}
                                        className="custom-btn"
                                        style={{
                                          width: '90%',
                                          padding: '13px 18px',
                                          textAlign: 'left',
                                          fontSize: '0.95rem',
                                          lineHeight: 1.3,
                                          border: 'none',
                                          outline: 'none',
                                          //  activeInsightSection === label ? '2px solid #1f334f' : '2px solid transparent',
                                          background: activeInsightSection === label ? '#0e5aa7' : 'transparent',
                                          color: activeInsightSection === label ? '#ffffff' : '#1f2f46',
                                          cursor: 'pointer',
                                          fontWeight: activeInsightSection === label ? 600 : 500,
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          borderRadius: activeInsightSection === label ? 18 : 0,
                                          margin: `${itemIndex === 0 ? '8px auto 0px' : '0 auto'}`,
                                          // marginTop: itemIndex === 0 ? 8 : 0,
                                          boxShadow:
                                            activeInsightSection === label
                                              ? '0 2px 6px rgba(15, 23, 42, 0.14)'
                                              : 'none',
                                        }}
                                      >
                                        <ItemIcon
                                          sx={{
                                            fontSize: 22,
                                            color: activeInsightSection === label ? '#ffffff' : '#1f2f46',
                                            flexShrink: 0,
                                          }}
                                        />
                                        <span style={{ flex: 1 }}>{displayLabel}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {isMiddleEastMarket && !propSetActiveInsightSection && isMobile && (
                                  <Drawer
                                    anchor='left'
                                    open={isInsightsNavOpen}
                                    onClose={() => setIsInsightsNavOpen(false)}
                                    ModalProps={{
                                      keepMounted: true,
                                      disableEnforceFocus: true,
                                      disableAutoFocus: true,
                                      disableRestoreFocus: true,
                                      sx: {
                                        top: '96px',
                                        height: 'calc(100% - 96px)',
                                      },
                                    }}
                                    BackdropProps={{
                                      sx: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      },
                                    }}
                                    PaperProps={{
                                      sx: {
                                        width: '78vw',
                                        maxWidth: 300,
                                        top: 0,
                                        height: '100%',
                                        borderRight: '1px solid #d9e1eb',
                                        borderTopRightRadius: '16px',
                                        borderBottomRightRadius: '16px',
                                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                                        boxShadow: '8px 0 24px rgba(15, 23, 42, 0.18)',
                                        overflow: 'hidden',
                                      },
                                    }}
                                  >
                                    <Box sx={{ pt: 1, px: 0.5 }}>
                                      {visibleInsightSections.map(({ label, displayLabel, icon: ItemIcon }, itemIndex) => (
                                        <button
                                          key={`mobile-${label}`}
                                          onClick={() => {
                                            setActiveInsightSection(label);
                                            setIsInsightsNavOpen(false);
                                            callLogEvent('CLICKED_INSIGHT_SECTION', `Insight section clicked: ${label} (mobile)`);
                                          }}
                                          className='custom-btn'
                                          style={{
                                            width: '90%',
                                            padding: '13px 18px',
                                            textAlign: 'left',
                                            fontSize: '0.92rem',
                                            lineHeight: 1.3,
                                            border: 'none',
                                            outline: 'none',
                                            background: activeInsightSection === label ? '#0e5aa7' : 'transparent',
                                            color: activeInsightSection === label ? '#ffffff' : '#1f2f46',
                                            cursor: 'pointer',
                                            fontWeight: activeInsightSection === label ? 600 : 500,
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            borderRadius: activeInsightSection === label ? 18 : 0,
                                            margin: `${itemIndex === 0 ? '10px auto 2px' : '2px auto'}`,
                                            boxShadow:
                                              activeInsightSection === label
                                                ? '0 2px 6px rgba(15, 23, 42, 0.14)'
                                                : 'none',
                                          }}
                                        >
                                          <ItemIcon
                                            sx={{
                                              fontSize: 22,
                                              color: activeInsightSection === label ? '#ffffff' : '#1f2f46',
                                              flexShrink: 0,
                                            }}
                                          />
                                          <span style={{ flex: 1 }}>{displayLabel}</span>
                                        </button>
                                      ))}
                                    </Box>
                                  </Drawer>
                                )}
                                {/* Main content: accordion sections */}
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    minHeight: 0,
                                    padding: '5px 18px 18px 18px',
                                    background: '#edf1f6',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', borderRadius: 18, display: effectiveActiveInsightSection === 'PERFORMANCE TREE' ? 'flex' : 'none' }}>
                                      <TreeVisualization />
                                  </div>
                                  <div style={{ flex: 1, minHeight: 0, display: effectiveActiveInsightSection !== 'PERFORMANCE TREE' ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
                                  {isMiddleEastMarket && !propSetActiveInsightSection && visibleInsightSections.length > 0 && (
                                    <div
                                      style={{
                                        marginBottom: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                      }}
                                    >
                                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0b1323', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {String(effectiveActiveInsightSection || 'KEY TAKEAWAYS')
                                          .toLowerCase()
                                          .replace(/\b\w/g, (char: string) => char.toUpperCase())}
                                        {(() => {
                                          const helpText = getInsightSectionHelpText(effectiveActiveInsightSection || 'KEY TAKEAWAYS');
                                          if (!helpText) return null;
                                          return (
                                            <Tooltip
                                              title={
                                                <Box sx={{ p: 0.25 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                                    <InfoOutlinedIcon sx={{ fontSize: 13, color: '#93c5fd', flexShrink: 0 }} />
                                                    <Box component='span' sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#bfdbfe', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                                      About this section
                                                    </Box>
                                                  </Box>
                                                  <Box sx={{ fontSize: '0.78rem', lineHeight: 1.6, color: '#e2e8f0' }}>
                                                    {helpText}
                                                  </Box>
                                                </Box>
                                              }
                                              arrow
                                              placement='bottom'
                                              componentsProps={{
                                                tooltip: {
                                                  sx: {
                                                    maxWidth: 320,
                                                    p: 1.25,
                                                    borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                                    border: '1px solid rgba(99,179,237,0.2)',
                                                    boxShadow: '0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(99,179,237,0.08)',
                                                  },
                                                },
                                                arrow: { sx: { color: '#1e293b' } },
                                              }}
                                            >
                                              <Box
                                                component='span'
                                                sx={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  width: 24,
                                                  height: 24,
                                                  borderRadius: '50%',
                                                  // border: '1.5px solid #1a1a1a',
                                                  color: '#1a1a1a',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s ease',
                                                  flexShrink: 0,
                                                  '&:hover': {
                                                    background: 'rgba(0,0,0,0.08)',
                                                  },
                                                }}
                                              >
                                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                                              </Box>
                                            </Tooltip>
                                          );
                                        })()}
                                      </div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          justifyContent: 'flex-end',
                                          gap: 8,
                                          background: 'rgba(255,255,255,0.9)',
                                          border: '1px solid #dbe5f1',
                                          borderRadius: 18,
                                          padding: '4px 6px',
                                          backdropFilter: 'blur(2px)',
                                          marginLeft: 'auto',
                                          zIndex: 21,
                                        }}
                                      >
                                        <div className={styles.feedback} style={{ top: 0, marginTop: 0 }}>
                                          <AdditionalOptions
                                            msg={msg}
                                            refId={answerRef.current}
                                            questionRefs={questionRefs?.current[index - 1]}
                                            handleVisualization={handleCharts}
                                            handleSavedChats={() => handleSaveChat(msg, index)}
                                          />
                                          {msg.is_liked ? (
                                            <FillThumbsUp
                                              onClick={msg.is_disliked ? undefined : () => handleFeedback(msg, index)}
                                              className={'cursor-pointer imgSize14 mr15'}
                                            />
                                          ) : (
                                            <ThumbsUp
                                              onClick={msg.is_disliked ? undefined : () => handleFeedback(msg, index)}
                                              className={'cursor-pointer imgSize14 mr15'}
                                            />
                                          )}
                                          {msg.is_disliked ? (
                                            <FillThumbsDown className='imgSize14 cursor-pointer mr15' />
                                          ) : (
                                            <ThumbsDown
                                              onClick={async () => {
                                                try {
                                                  const extra = await getExtraInfoFromMessage(msg, index);
                                                  callLogEvent('CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON', 'Clicked Dislike message button' + (extra || ''));
                                                } catch (e) {
                                                  // ignore
                                                }
                                                handleFeedbackReason(msg, index);
                                              }}
                                              className='imgSize14 cursor-pointer mr15'
                                            />
                                          )}
                                          {/* <Tooltip title='Delete summary'>
                                            <IconButton
                                              size='small'
                                              onClick={() => handleDeleteSummary(msg, index)}
                                              aria-label='Delete'
                                              sx={{ color: '#d32f2f' }}
                                            >
                                              <DeleteOutlineIcon fontSize='small' />
                                            </IconButton>
                                          </Tooltip> */}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      border: '1px solid #d3dbe6',
                                      borderRadius: 18,
                                      padding: '22px 28px',
                                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                                      flex: 1,
                                      minHeight: 0,
                                      overflowY: 'auto',
                                      overscrollBehavior: 'contain',
                                    }}
                                  >
                                    {getSummaryStyles(msg, false, effectiveActiveInsightSection)}
                                  </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              maxHeight: 'auto',
                              overflow: 'visible',
                              marginBottom: 22,
                              paddingRight: 8,
                              paddingTop: 8,
                              display: 'flex',
                              flexDirection: 'column',
                              // border: '1px solid #d9e5f4',
                              border: '1px solid #d9e5f4',
                              borderLeft: '1px solid #d9e5f4',
                              borderRadius: 12,
                              background:
                                'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
                              boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
                              ...(Number(index) === latestSummaryIndex
                                ? {
                                  border: '2px solid #2f88ff',
                                  borderLeft: '2px solid #2f88ff',
                                  boxShadow:
                                    '0 2px 10px rgba(47,136,255,0.16)',
                                }
                                : {}),
                            }}
                          >
                            {/* Top row: Number badge | Content | Action buttons */}
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap:0,
                                padding: '12px 12px 12px 12px',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 12,
                                  width: '100%',
                                }}
                              >
                                {/* Number badge */}
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    paddingTop: 0,
                                    alignSelf: 'flex-start',
                                  }}
                                >
                                  {/* <span
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      background: '#005dd9fa',
                                      color: '#ffffff',
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 4px 10px rgba(47,136,255,0.25)',
                                    }}
                                  >
                                    {getSummaryOrdinal(Number(index))}
                                  </span> */}
                                </div>
                                {/* Action buttons */}
                                {(() => {
                                  return (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: 8,
                                        background: 'rgba(255,255,255,0.88)',
                                        border: '1px solid #e6eef8',
                                        borderRadius: 18,
                                        padding: '2px 4px',
                                        backdropFilter: 'blur(2px)',
                                        flexShrink: 0,
                                        alignSelf: 'flex-start',
                                        width: 'auto',
                                        marginLeft: 'auto',
                                      }}
                                    >
                                      <div className={styles.feedback} style={{ top: 0, marginTop: 0 }}>
                                        <AdditionalOptions
                                          msg={msg}
                                          refId={answerRef.current}
                                          questionRefs={questionRefs?.current[index - 1]}
                                          handleVisualization={handleCharts}
                                          handleSavedChats={() => handleSaveChat(msg, index)}
                                        />
                                        {msg.is_liked ? (
                                          <FillThumbsUp
                                            onClick={
                                              msg.is_disliked
                                                ? undefined
                                                : () => handleFeedback(msg, index)
                                            }
                                            className={'cursor-pointer imgSize14 mr15'}
                                          />
                                        ) : (
                                          <ThumbsUp
                                            onClick={
                                              msg.is_disliked
                                                ? undefined
                                                : () => handleFeedback(msg, index)
                                            }
                                            className={'cursor-pointer imgSize14 mr15'}
                                          />
                                        )}
                                        {msg.is_disliked ? (
                                          <FillThumbsDown className='imgSize14 cursor-pointer mr15' />
                                        ) : (
                                          <ThumbsDown
                                            onClick={async () => {
                                              try {
                                                const extra = await getExtraInfoFromMessage(
                                                  msg,
                                                  index
                                                );
                                                callLogEvent(
                                                  'CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON',
                                                  'Clicked Dislike message button' +
                                                    (extra || '')
                                                );
                                              } catch (e) {
                                                // ignore
                                              }
                                              handleFeedbackReason(msg, index);
                                            }}
                                            className='imgSize14 cursor-pointer mr15'
                                          />
                                        )}
                                        {/* <Tooltip title='Delete summary'>
                                          <IconButton
                                            size='small'
                                            onClick={() =>
                                              handleDeleteSummary(msg, index)
                                            }
                                            aria-label='Delete'
                                            sx={{ color: '#d32f2f' }}
                                          >
                                            <DeleteOutlineIcon fontSize='small' />
                                          </IconButton>
                                        </Tooltip> */}
                                      </div>
                                      <button
                                        className={styles.selectAllButton}
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (isTransitioningView) return;
                                          setIsTransitioningView(true);
                                          try {
                                            const extra =
                                              await getExtraInfoFromMessage(
                                                msg,
                                                index
                                              );
                                            lastManualClosedSummaryIndexRef.current =
                                              null;
                                            setSummaryIndex(Number(index));
                                            callLogEvent(
                                              'CLICKED_NEW_DIALOG_SUMMARY_OPEN_BUTTON',
                                              'Clicked Open on collapsed summary' +
                                                (extra || '')
                                            );
                                          } finally {
                                            setIsTransitioningView(false);
                                          }
                                        }}
                                        disabled={isTransitioningView}
                                        style={{
                                          padding: '4px 10px',
                                          minHeight: 28,
                                          fontSize: '0.85rem',
                                          minWidth: 20,
                                          textTransform: 'none',
                                          opacity: isTransitioningView ? 0.6 : 1,
                                          cursor: isTransitioningView ? 'not-allowed' : 'pointer',
                                        }}
                                      >
                                        Open
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                              {/* Content */}
                              <div style={{ width: '100%', minWidth: 0 }}>
                                {msg?.is_deleted ? (
                                  <div style={{ padding: 12, minHeight: 80 }}>
                                    &nbsp;
                                  </div>
                                ) : (
                                  getSummaryStyles(msg, true)
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* <span className={styles.feedback}>
											{msg.is_liked ? (
												<FillThumbsUp
													onClick={
														msg.is_disliked
															? undefined
															: () => handleFeedback(msg, index)
													}
													className={'cursor-pointer imgSize14 mr15'}
												/>
											) : (
												<ThumbsUp
													onClick={
														msg.is_disliked
															? undefined
															: () => handleFeedback(msg, index)
													}
													className={'cursor-pointer imgSize14 mr15'}
												/>
											)}
											{msg.is_disliked ? (
												<FillThumbsDown className='imgSize14 mr15' />
											) : (
												<ThumbsDown
													onClick={() => {
														callLogEvent(
															'CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON',
															'Clicked Dislike message botton'
														);
														handleFeedbackReason(msg, index);
													}}
													className='imgSize14 cursor-pointer mr15'
												/>
											)}
										</span> */}
                        {/* <span className={styles.aiavatar}>
											<img
												// src={aiavatar}
												alt='avatar'
											/>
										</span> */}
                      </div>
                    </>
                  ) : msg?.sender !== 123 &&
                    msg?.sender !== 'summary' &&
                    uiMode === 'chat' ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        // marginBottom: 12,
                        width: '100%',
                      }}
                    >
                      {/* Left avatar: chat bot icon */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          minWidth: 24,
                          minHeight: 24,
                          flexShrink: 0,
                          marginTop: 4,
                        }}
                        aria-hidden
                      >
                        <ChatBotIcon style={{ width: 20, height: 20 }} />
                      </span>
                      {/* Response bubble */}
                      <div
                        className={styles.receivedMessage}
                        ref={answerRef}
                        style={{
                          marginTop: 4,
                          flex: 1,
                          minWidth: 0,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          width: '100%',
                        }}
                      >
                        {msg.chatMessage}
                        {msg.images ? msg.images : ''}
                        <br />

                        {/* <span className={styles.feedback}>
											{msg.is_liked ? (
												<FillThumbsUp
													onClick={
														msg.is_disliked
															? undefined
															: () => handleFeedback(msg, index)
													}
													className={'cursor-pointer imgSize14 mr15'}
												/>
											) : (
												<ThumbsUp
													onClick={
														msg.is_disliked
															? undefined
															: () => handleFeedback(msg, index)
													}
													className={'cursor-pointer imgSize14 mr15'}
												/>
											)}
											{msg.is_disliked ? (
												<FillThumbsDown className='imgSize14 mr15' />
											) : (
												<ThumbsDown
													onClick={() => {
														callLogEvent(
															'CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON',
															'Clicked Dislike message botton'
														);
														handleFeedbackReason(msg, index);
													}}
													className='imgSize14 cursor-pointer mr15'
												/>
											)}
										</span> */}
                        {/* <span className={styles.aiavatar}>
											<img
												// src={aiavatar}
												alt='avatar'
											/>
										</span> */}
                      </div>
                    </div>
                  ) : null}

                  {/* <span className={styles.aiavatar}>
										<img
											src={aiavatar}
											alt='avatar'
										/>
									</span> */}
                  {/* Action bar: show in chat mode for AI Q&A messages */}
                  {uiMode === 'chat' &&
                    msg?.sender !== 123 &&
                    msg?.sender !== 'summary' && (
                      <div className={styles.actionBar}>
                        <div className={styles.feedback}>
                          <AdditionalOptions
                            msg={msg}
                            refId={answerRef.current}
                            questionRefs={questionRefs?.current[index - 1]}
                            handleVisualization={handleCharts}
                            handleSavedChats={() => handleSaveChat(msg, index)}
                          />
                          {msg.is_liked ? (
                            <FillThumbsUp
                              onClick={
                                msg.is_disliked
                                  ? undefined
                                  : () => handleFeedback(msg, index)
                              }
                              className={'cursor-pointer imgSize14 mr15'}
                            />
                          ) : (
                            <ThumbsUp
                              onClick={
                                msg.is_disliked
                                  ? undefined
                                  : () => handleFeedback(msg, index)
                              }
                              className={'cursor-pointer imgSize14 mr15'}
                            />
                          )}
                          {msg.is_disliked ? (
                            <FillThumbsDown className='imgSize14 cursor-pointer mr15' />
                          ) : (
                            <ThumbsDown
                              onClick={async () => {
                                try {
                                  const extra = await getExtraInfoFromMessage(
                                    msg,
                                    index
                                  );
                                  callLogEvent(
                                    'CLICKED_NEW_DIALOG_MESSAGE_DISLIKE_BUTTON',
                                    'Clicked Dislike message button' +
                                    (extra || '')
                                  );
                                } catch (e) {
                                  // ignore
                                }
                                handleFeedbackReason(msg, index);
                              }}
                              className='imgSize14  cursor-pointer mr15'
                            />
                          )}
                        </div>
                      </div>
                    )}

                  {/* Action bar for summary items moved inside the expanded card footer */}

                  {/* Suggestions block and some related UI temporarily removed to maintain file stability. */}
                </>
              )}
            </div>
              );
            })()
          ))}
          {uiMode === 'chat' && !isIdeal && (
            <div className={styles.outerDiv}>
              <StreamingBubble isIdeal={isIdeal} />
            </div>
          )}
          {/* {messages.length === 0 &&
				!(localStorage.getItem('DataSource') === 'SpendWise') && (
					<div className={styles.noMessages}>
						<span className={styles.suggestionIcon}>
							<ReorderIcon />
						</span>
						<p style={{paddingTop: '10px'}}>Suggestions</p>
						{R_AND_D_AUTO_SUGGESTIONS.map(
							(x: any, suggestionIndex: number) =>
								suggestionIndex < 4 && (
									<p
										className={styles.suggestionBtn}
										key={suggestionIndex}
									>
										<button
											onClick={() =>
												!isIdeal
													? undefined
													: (callLogEvent(
															'CLICKED_NEW_DIALOG_SUGGESTION',
															'Clicked on one of the suggestions: ' + x
													  ),
													  setLatestSuggestions(x, R_AND_D_AUTO_SUGGESTIONS))
											}
										>
											{x}
										</button>
									</p>
								)
						)}
					</div>
				)} */}
        </div>
        <div ref={messagesEndRef} /> {/* This empty div is for scrolling */}
        {/* <div className={styles.scrollButtonsContainer}>
          <button className={styles.scrollButton} onClick={scrollToTop}>
            ↑
          </button>
          <button className={styles.scrollButton} onClick={scrollToBottom}>
            ↓
          </button>
        </div> */}
        {/* Button to toggle filter drawer
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsFilterDrawerOpen(true)}
          style={{ margin: "10px" }}
        >
          Show Filters
        </Button> */}
        {/* Drawer for filter options */}
        {uiMode === 'summary' && (
          <Dialog
            fullWidth
            maxWidth='md'
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                width: { xs: '96vw', sm: 'min(1080px, 94vw)' },
                maxWidth: 'unset',
                borderRadius: { xs: 2, sm: 3 },
                overflow: 'hidden',
                border: '1px solid #e3ecf8',
                boxShadow: '0 18px 46px rgba(15, 23, 42, 0.16)',
                background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 18%)',
                maxHeight: { xs: 'calc(100dvh - 8px)', sm: 'calc(100dvh - 24px)' },
                height: { xs: 'calc(100dvh - 8px)', sm: 'auto' },
                display: 'flex',
                flexDirection: 'column',
                ...(isMobile && { marginTop: '4px' }),

              },
            }}
            // PaperProps={{ sx: { maxWidth: 'fit-content' } }}
            open={isFilterDrawerOpen}
            onClose={() => {
              setIsFilterDrawerOpen(false);
              callLogEvent('CLOSED_GENERATE_INSIGHTS', 'Generate Insights drawer closed');
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 1.25, sm: 1.75 },
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 6,
                  position: 'relative',
                }}
              >
                <Typography
                  variant='h6'
                  style={{
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: '1rem',
                    color: '#12304f',
                  }}
                >
                  Customize Your Insights
                </Typography>
                <IconButton
                  onClick={() => {
                    setIsFilterDrawerOpen(false);
                    callLogEvent('CLOSED_GENERATE_INSIGHTS', 'Generate Insights drawer closed');
                  }}
                  edge='end'
                  className='filter_btn'
                  style={{
                    position: 'absolute',
                    right: isMobile ? '8px' : '0px',
                    top: isMobile ? '44%' : '40%',
                    transform: 'translateY(-50%)',
                    // background: '#edf3fc',
                    border: '1px solid #dde9f8',
                  }}
                >
                  <CloseIcon className={styles.closeIcon}/>
                </IconButton>
              </div>
              <Box
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  backgroundColor: '#f5f9ff',
                  borderRadius: 2,
                  border: '1px solid #e4eefb',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: { xs: 0, sm: 0.5 },
                  }}
                >
                  {getFilterStyles2(true)}
                </Box>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: 10,
                  }}
                >
                  <Button
                    variant='contained'
                    onClick={() => {
                      handleFilterSummary();
                      setIsFilterDrawerOpen(false);
                      callLogEvent('CLOSED_GENERATE_INSIGHTS', 'Generate Insights applied and closed');
                    }}
              sx={{
                      backgroundImage: '#005bbd',
                      backgroundColor: '#005bbd',
                      borderRadius: '8px',
                      height: '40px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      padding: '0 20px',
                      '&:hover': {
                        backgroundImage: '#005bbd',
                      },
                    }}

                    disabled={
                      syncFilters ||
                      isSummaryGenerating ||
                      disableSummaryBtn ||
                      !filtersApplied
                    }
                  >
                    {syncFilters || isSummaryGenerating || disableSummaryBtn ? (
                      <CircularProgress
                        size={22}
                        disableShrink
                        sx={{ color: 'white' }}
                      />
                    ) : (
                      'Generate Insights'
                    )}
                  </Button>
                  {/* help icon removed from inside the filter component; summary-level help remains */}
                </div>
              </Box>
            </Box>
          </Dialog>
        )}

        <Drawer
          anchor='left'
          open={isSummaryHistoryOpen}
          onClose={() => {
            setIsSummaryHistoryOpen(false);
            callLogEvent('CLOSED_HISTORY', 'History panel closed');
          }}
          PaperProps={{
            sx: {
              width: { xs: '86vw', sm: 420 },
              maxWidth: '95vw',
              background: '#f8fafd',
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              color: '#fff',
              background: 'linear-gradient(90deg, #1f2f49 0%, #334868 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon sx={{ fontSize: 24 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.4rem' }}>History</Typography>
              </Box>
              <IconButton onClick={() => {
                setIsSummaryHistoryOpen(false);
                callLogEvent('CLOSED_HISTORY', 'History panel closed');
              }} sx={{ color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography sx={{ mt: 0.5, opacity: 0.85, fontSize: '0.95rem' }}>
              All summaries and reports
            </Typography>
          </Box>

          <Box sx={{ p: 2, overflowY: 'auto' }}>
            {summaryHistoryItems.length === 0 ? (
              <Typography sx={{ color: '#6b7b8f', fontSize: '0.95rem' }}>
                No summaries available.
              </Typography>
            ) : (
              summaryHistoryItems
                .sort((a: any, b: any) => b.idx - a.idx) // Sort descending (newest first)
                .map(({ msg, idx }: any) => {
                  const isCurrentlySelected = Number(idx) === Number(activeSummaryIndex);
                  const isLatest = Number(idx) === Number(latestSummaryIndex);

                  return (
                    <Box
                      key={msg?.request_id || `history-${idx}`}
                      onClick={() => {
                        setSummaryIndex(Number(idx));
                        setActiveInsightSection('KEY TAKEAWAYS');
                        setIsSummaryHistoryOpen(false);
                        callLogEvent(
                          'CLICKED_SUMMARY_HISTORY_ITEM',
                          `Opened summary index ${getSummaryOrdinal(Number(idx))}`
                        );
                      }}
                      sx={{
                        mb: 1.5,
                        p: 1.6,
                        borderRadius: '16px',
                        border: isCurrentlySelected ? '2px solid #2f88ff' : '1px solid #d9e4f2',
                        background: isCurrentlySelected ? '#f0f6ff' : '#fff',
                        cursor: 'pointer',
                        boxShadow: isCurrentlySelected
                          ? '0 4px 12px rgba(47,136,255,0.2)'
                          : '0 1px 3px rgba(15,23,42,0.04)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: isCurrentlySelected ? '#2f88ff' : '#b7cce8',
                          background: isCurrentlySelected ? '#f0f6ff' : '#fafdff',
                          boxShadow: isCurrentlySelected
                            ? '0 4px 12px rgba(47,136,255,0.2)'
                            : '0 2px 6px rgba(15,23,42,0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: isCurrentlySelected ? '#2f88ff' : '#1f2b3d', fontSize: '0.9rem' }}>
                          Summary #{getSummaryOrdinal(Number(idx))}
                        </Typography>
                        {isCurrentlySelected && (
                          <Box sx={{
                            px: 1,
                            py: 0.4,
                            background: '#2f88ff',
                            color: '#fff',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}>
                            Current
                          </Box>
                        )}
                        {isLatest && !isCurrentlySelected && (
                          <Box sx={{
                            px: 1,
                            py: 0.4,
                            background: '#10b981',
                            color: '#fff',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}>
                            Latest
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ mt: 0.8, color: isCurrentlySelected ? '#1f2b3d' : '#607089', fontSize: '11px' }}>
                        {getHistoryPreviewText(msg)}
                      </Typography>
                    </Box>
                  );
                })
            )}
          </Box>
        </Drawer>
      </div>
      {/* Filter Help Modal */}
      <FilterHelp
        open={showFilterHelp}
        onClose={() => setShowFilterHelp(false)}
      />
    </>
  );
};

export default MessageList;
